import React, { useState, useEffect, useRef } from 'react';
import { Settings, Printer, Monitor, RefreshCw, Building, Upload, Trash2, AlertTriangle, Globe, CheckCircle, XCircle, Loader, ShieldCheck, Scale, Cloud, Play, Pause, Clock, ArrowUpCircle, ArrowDownCircle, RotateCcw, Palette, Receipt, FileText } from 'lucide-react';
import { formatMoney } from '../../utils/formatters';
import { useApp } from '../../context/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useSync } from '../../context/SyncContext';
import apiService from '../../services/apiService';
import Button from '../../components/ui/Button';
import MiniRichEditor from '../../components/ui/MiniRichEditor';
import WebModeConfig from '../../components/settings/WebModeConfig';
import './SettingsPage.css';

const SettingsPage = () => {
  const { setLoading, showToast, isWebMode } = useApp();
  const { hasPermission } = usePermissions();
  const [configuraciones, setConfiguraciones] = useState({});
  const [configOriginal, setConfigOriginal] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Permisos para cada tab
  const canViewCaja = hasPermission('configuracion_ver');
  const canViewAdmin = hasPermission('configuracion_admin');
  const canConfigFacturacion = hasPermission('facturacion_config');

  // Tab activo - seleccionar el primer tab disponible
  const [activeTab, setActiveTab] = useState(canViewCaja ? 'caja' : canViewAdmin ? 'admin' : 'caja');
  const [adminSubTab, setAdminSubTab] = useState(isWebMode ? 'web-mode' : 'punto-venta');
  const [cajaSubTab, setCajaSubTab] = useState('configuracion');

  // Ref para el input de archivo del logo
  const logoInputRef = useRef(null);

  // Configuración de punto de venta
  const [infoPc, setInfoPc] = useState({
    nombrePc: '',
    ip: '',
    identificador: ''
  });
  const [identificadorOriginal, setIdentificadorOriginal] = useState('');
  const [loadingInfoPc, setLoadingInfoPc] = useState(false);

  // Impresoras disponibles
  const [impresoras, setImpresoras] = useState([]);
  const [loadingImpresoras, setLoadingImpresoras] = useState(false);

  // Configuración del API
  const [apiUrl, setApiUrl] = useState('');
  const [apiUrlOriginal, setApiUrlOriginal] = useState('');
  const [testingApi, setTestingApi] = useState(false);
  const [apiTestResult, setApiTestResult] = useState(null); // null, 'success', 'error'

  // Báscula serial
  const [scalePorts, setScalePorts] = useState([]);
  const [scaleConnected, setScaleConnected] = useState(false);
  const [scaleConnecting, setScaleConnecting] = useState(false);
  const [scaleWeight, setScaleWeight] = useState(null);
  const [readingWeight, setReadingWeight] = useState(false);
  const [scaleStatus, setScaleStatus] = useState('');

  // API Nube
  const [testingApiNube, setTestingApiNube] = useState(false);
  const [apiNubeTestResult, setApiNubeTestResult] = useState(null);

  // Sync operativo (usa contexto global para auto-sync)
  const syncCtx = useSync();
  const [syncing, setSyncing] = useState(false);
  const [syncOperation, setSyncOperation] = useState(''); // 'push', 'pull', 'full'
  const [syncResult, setSyncResult] = useState(null);
  const [syncStatus, setSyncStatusData] = useState(null);
  const [syncLog, setSyncLog] = useState([]);
  const [loadingSyncStatus, setLoadingSyncStatus] = useState(false);

  // Emisores CFDI (múltiples)
  const [emisores, setEmisores] = useState([]);
  const [loadingEmisores, setLoadingEmisores] = useState(false);
  const [nuevoEmisor, setNuevoEmisor] = useState({ llave: '', rfc: '', razon_social: '' });
  const [emisorErrors, setEmisorErrors] = useState({});
  const [savingEmisor, setSavingEmisor] = useState(false);
  const [editandoEmisor, setEditandoEmisor] = useState(null); // id del emisor en edición

  // Cargar info del PC primero, luego configuraciones
  useEffect(() => {
    obtenerInfoPc();
    cargarApiUrl();
    cargarImpresoras();
  }, []);

  // Cargar lista de impresoras del sistema
  const cargarImpresoras = async () => {
    if (!window.electron?.ipcRenderer) return;
    setLoadingImpresoras(true);
    try {
      const result = await window.electron.ipcRenderer.invoke('get-all-printers');
      if (result.success && result.printers) {
        setImpresoras(result.printers);
      } else if (Array.isArray(result)) {
        setImpresoras(result);
      }
    } catch (e) {
      console.error('Error cargando impresoras:', e);
    } finally {
      setLoadingImpresoras(false);
    }
  };

  // Detectar cambios (configuraciones + identificador de caja + API URL)
  useEffect(() => {
    const configChanged = JSON.stringify(configuraciones) !== JSON.stringify(configOriginal);
    const identificadorChanged = infoPc.identificador !== identificadorOriginal;
    const apiUrlChanged = apiUrl !== apiUrlOriginal;
    setHasChanges(configChanged || identificadorChanged || apiUrlChanged);
  }, [configuraciones, configOriginal, infoPc.identificador, identificadorOriginal, apiUrl, apiUrlOriginal]);

  // Cargar URL del API
  const cargarApiUrl = async () => {
    try {
      // Esperar a que el apiService se inicialice
      await new Promise(resolve => setTimeout(resolve, 100));
      const currentUrl = apiService.getApiUrl();
      setApiUrl(currentUrl);
      setApiUrlOriginal(currentUrl);
    } catch (error) {
      console.error('Error cargando API URL:', error);
    }
  };

  // Probar conexión con el API
  const handleTestApiConnection = async () => {
    if (!apiUrl.trim()) {
      showToast('Ingresa una URL válida', 'error');
      return;
    }

    setTestingApi(true);
    setApiTestResult(null);

    try {
      const result = await apiService.testApiConnection(apiUrl.trim());
      setApiTestResult(result.success ? 'success' : 'error');
      if (result.success) {
        showToast('Conexión exitosa al API', 'success');
      } else {
        showToast(result.message || 'No se pudo conectar al API', 'error');
      }
    } catch (error) {
      setApiTestResult('error');
      showToast('Error al probar conexión', 'error');
    } finally {
      setTestingApi(false);
    }
  };

  // Restaurar URL por defecto
  const handleRestoreDefaultApiUrl = () => {
    const defaultUrl = apiService.getDefaultUrl();
    setApiUrl(defaultUrl);
    setApiTestResult(null);
  };

  const obtenerInfoPc = async () => {
    setLoadingInfoPc(true);
    try {
      let nombrePc = '';
      let ip = '';

      // Intentar obtener info desde Electron
      if (window.electron && window.electron.ipcRenderer) {
        try {
          console.log('Intentando obtener info del sistema desde Electron...');
          const result = await window.electron.ipcRenderer.invoke('get-system-info');
          console.log('Resultado get-system-info:', result);
          if (result && result.success) {
            nombrePc = result.hostname || '';
            ip = result.ip || '';
          }
        } catch (e) {
          console.error('Error obteniendo info del sistema desde Electron:', e);
        }
      } else {
        console.log('Electron IPC no disponible');
      }

      // Fallback: usar valor guardado en localStorage si no se obtuvo
      if (!nombrePc) {
        nombrePc = localStorage.getItem('pos_nombre_pc') || '';
        console.log('Usando nombrePc desde localStorage:', nombrePc);
      }

      // Si aún no hay nombrePc, intentar generar uno único basado en fingerprint
      if (!nombrePc) {
        // Generar un ID único para este navegador/dispositivo
        const storedId = localStorage.getItem('pos_device_id');
        if (storedId) {
          nombrePc = storedId;
        } else {
          // Crear un ID único
          const uniqueId = 'PC_' + Math.random().toString(36).substr(2, 9).toUpperCase();
          localStorage.setItem('pos_device_id', uniqueId);
          nombrePc = uniqueId;
        }
        console.log('Usando ID generado:', nombrePc);
      }

      // Si no hay IP, intentar obtener IP desde API externa
      if (!ip) {
        try {
          const response = await fetch('https://api.ipify.org?format=json');
          const data = await response.json();
          ip = data.ip || '';
        } catch (e) {
          console.log('No se pudo obtener IP externa');
          ip = 'No disponible';
        }
      }

      // Guardar nombrePc en localStorage para uso en otras páginas
      if (nombrePc) {
        localStorage.setItem('pos_nombre_pc', nombrePc);
      }

      setInfoPc(prev => ({ ...prev, nombrePc, ip }));

      // Buscar si ya existe una caja para este equipo
      if (nombrePc) {
        try {
          const cajaResult = await apiService.getCajaPorNombrePc(nombrePc);
          if (cajaResult.success && cajaResult.data) {
            const identificador = cajaResult.data.identificador || '';
            setInfoPc(prev => ({
              ...prev,
              identificador
            }));
            setIdentificadorOriginal(identificador);
          }
        } catch (e) {
          console.log('No hay caja registrada para este equipo');
        }
      }

      // Cargar configuraciones con el nombrePc
      await cargarConfiguraciones(nombrePc);

    } catch (error) {
      console.error('Error obteniendo info PC:', error);
    } finally {
      setLoadingInfoPc(false);
    }
  };

  const cargarConfiguraciones = async (nombrePc = null) => {
    setLoading(true);
    try {
      const pc = nombrePc || infoPc.nombrePc || localStorage.getItem('pos_nombre_pc');

      // Cargar configuraciones:
      // - empresa: GLOBAL (sin nombrePc)
      // - sync: GLOBAL (sin nombrePc) - config de nube
      // - impresion: por caja (con nombrePc)
      // - caja: por caja (con nombrePc)
      const [empresaResult, syncResult, impresionResult, cajaResult] = await Promise.all([
        apiService.getConfiguracionesPorCategoria('empresa', null), // Global
        apiService.getConfiguracionesPorCategoria('sync', null),    // Global - nube
        apiService.getConfiguracionesPorCategoria('impresion', pc), // Por caja
        apiService.getConfiguracionesPorCategoria('caja', pc)       // Por caja
      ]);

      const valores = {
        // Valores por defecto para configuraciones de caja
        caja_alerta_efectivo_maximo: 0,
        caja_dia_entrega_pedido: '0',
        caja_bascula_puerto: '',
        caja_bascula_baudrate: '9600',
        caja_api_nube_url: '',
        caja_api_nube_negocio: '',
        caja_api_nube_sucursal: '',
        caja_api_nube_auto_sync: '0',
        caja_api_nube_auto_sync_interval: '5',
        // Valores cargados de la API
        ...(empresaResult.success ? empresaResult.data.valores : {}),
        ...(syncResult.success ? syncResult.data.valores : {}),
        ...(impresionResult.success ? impresionResult.data.valores : {}),
        ...(cajaResult.success ? cajaResult.data.valores : {})
      };

      setConfiguraciones(valores);
      setConfigOriginal(valores);

      if (!empresaResult.success && !impresionResult.success && !cajaResult.success) {
        showToast('Error al cargar configuraciones', 'error');
      }
    } catch (error) {
      console.error('Error cargando configuraciones:', error);
      showToast('Error al cargar configuraciones', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (clave, valor) => {
    setConfiguraciones(prev => ({
      ...prev,
      [clave]: valor
    }));
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const nombrePc = infoPc.nombrePc || localStorage.getItem('pos_nombre_pc');

      // Separar configuraciones globales (empresa) de las de caja (impresion, caja_*)
      const configsGlobales = []; // empresa_logo, etc.
      const configsPorCaja = [];  // impresion_*, caja_*, etc.

      Object.entries(configuraciones).forEach(([clave, valor]) => {
        // Normalizar valor: convertir undefined/null a valor apropiado
        let valorNormalizado = valor;
        if (valor === undefined || valor === null) {
          valorNormalizado = '';
        }

        // Solo procesar configuraciones con prefijos conocidos
        if (clave.startsWith('empresa_') || clave.startsWith('caja_api_nube_')) {
          // empresa_* y caja_api_nube_* se guardan como globales (sin caja)
          configsGlobales.push({ clave, valor: valorNormalizado });
        } else if (clave.startsWith('impresion_') || clave.startsWith('caja_')) {
          // impresion_* y caja_* se guardan por caja
          configsPorCaja.push({ clave, valor: valorNormalizado });
        }
        // Ignorar otras claves que no tienen prefijo conocido
      });

      // Guardar en paralelo: globales sin nombrePc, por caja con nombrePc
      const promises = [];
      if (configsGlobales.length > 0) {
        promises.push(apiService.actualizarConfiguraciones(configsGlobales, null));
      }
      if (configsPorCaja.length > 0 && nombrePc) {
        promises.push(apiService.actualizarConfiguraciones(configsPorCaja, nombrePc));
      } else if (configsPorCaja.length > 0 && !nombrePc) {
        console.warn('No se pueden guardar configuraciones por caja sin nombrePc');
      }

      // Guardar identificador de caja si cambió
      if (infoPc.identificador !== identificadorOriginal && nombrePc) {
        promises.push(apiService.registrarCaja(infoPc.identificador, nombrePc, infoPc.ip));
      }

      // Guardar URL del API si cambió
      if (apiUrl !== apiUrlOriginal) {
        promises.push(apiService.saveApiUrl(apiUrl.trim()));
      }

      if (promises.length === 0) {
        showToast('No hay cambios para guardar', 'info');
        setSaving(false);
        return;
      }

      const results = await Promise.all(promises);
      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        setConfigOriginal({ ...configuraciones });
        setIdentificadorOriginal(infoPc.identificador);
        setApiUrlOriginal(apiUrl);
        showToast('Configuraciones guardadas correctamente', 'success');
      } else {
        // Mostrar detalles del error si están disponibles
        const errores = results.filter(r => !r.success).map(r => r.message).join(', ');
        console.error('Errores al guardar:', errores);
        showToast('Error al guardar algunas configuraciones', 'error');
      }
    } catch (error) {
      console.error('Error guardando configuraciones:', error);
      showToast('Error al guardar configuraciones', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDescartar = () => {
    setConfiguraciones({ ...configOriginal });
    setInfoPc(prev => ({ ...prev, identificador: identificadorOriginal }));
    setApiUrl(apiUrlOriginal);
    setApiTestResult(null);
    showToast('Cambios descartados', 'info');
  };

  // Manejar cambio de logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar formato
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      showToast('Solo se permiten imágenes JPG o PNG', 'error');
      e.target.value = '';
      return;
    }

    // Validar tamaño (500KB)
    if (file.size > 500 * 1024) {
      showToast('La imagen no debe superar 500KB', 'error');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      handleChange('empresa_logo', reader.result);
      showToast('Logo cargado correctamente', 'success');
    };
    reader.onerror = () => {
      showToast('Error al leer la imagen', 'error');
    };
    reader.readAsDataURL(file);
  };

  // Eliminar logo
  const handleRemoveLogo = () => {
    handleChange('empresa_logo', '');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    showToast('Logo eliminado', 'info');
  };

  // ===== FUNCIONES DE EMISORES CFDI =====
  const cargarEmisores = async () => {
    setLoadingEmisores(true);
    try {
      const result = await apiService.getEmisores();
      if (result.success) setEmisores(result.data || []);
    } catch (error) {
      console.error('Error cargando emisores:', error);
    } finally {
      setLoadingEmisores(false);
    }
  };

  const validarEmisor = (data) => {
    const errors = {};
    if (!data.llave.trim()) errors.llave = 'Llave es requerida';
    if (!data.rfc.trim()) {
      errors.rfc = 'RFC es requerido';
    } else {
      const len = data.rfc.trim().length;
      if (len !== 12 && len !== 13) errors.rfc = 'RFC debe tener 12 o 13 caracteres';
    }
    if (!data.razon_social.trim()) errors.razon_social = 'Razón social es requerida';
    return errors;
  };

  const handleAgregarEmisor = async () => {
    const errors = validarEmisor(nuevoEmisor);
    setEmisorErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavingEmisor(true);
    try {
      const result = await apiService.crearEmisor({
        llave: nuevoEmisor.llave.trim(),
        rfc: nuevoEmisor.rfc.toUpperCase().trim(),
        razon_social: nuevoEmisor.razon_social.trim()
      });
      if (result.success) {
        setNuevoEmisor({ llave: '', rfc: '', razon_social: '' });
        setEmisorErrors({});
        showToast('Emisor agregado', 'success');
        await cargarEmisores();
      } else {
        showToast(result.message || 'Error al agregar emisor', 'error');
      }
    } catch (error) {
      showToast('Error al agregar emisor', 'error');
    } finally {
      setSavingEmisor(false);
    }
  };

  const handleEditarEmisor = (emisor) => {
    setEditandoEmisor(emisor.id);
    setNuevoEmisor({ llave: emisor.llave, rfc: emisor.rfc, razon_social: emisor.razon_social });
    setEmisorErrors({});
  };

  const handleGuardarEdicion = async () => {
    const errors = validarEmisor(nuevoEmisor);
    setEmisorErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavingEmisor(true);
    try {
      const result = await apiService.actualizarEmisor(editandoEmisor, {
        llave: nuevoEmisor.llave.trim(),
        rfc: nuevoEmisor.rfc.toUpperCase().trim(),
        razon_social: nuevoEmisor.razon_social.trim()
      });
      if (result.success) {
        setEditandoEmisor(null);
        setNuevoEmisor({ llave: '', rfc: '', razon_social: '' });
        setEmisorErrors({});
        showToast('Emisor actualizado', 'success');
        await cargarEmisores();
      } else {
        showToast(result.message || 'Error al actualizar emisor', 'error');
      }
    } catch (error) {
      showToast('Error al actualizar emisor', 'error');
    } finally {
      setSavingEmisor(false);
    }
  };

  const handleCancelarEdicion = () => {
    setEditandoEmisor(null);
    setNuevoEmisor({ llave: '', rfc: '', razon_social: '' });
    setEmisorErrors({});
  };

  const handleEliminarEmisor = async (id) => {
    if (!window.confirm('¿Eliminar este emisor?')) return;
    try {
      const result = await apiService.eliminarEmisor(id);
      if (result.success) {
        showToast('Emisor eliminado', 'success');
        await cargarEmisores();
      } else {
        showToast(result.message || 'Error al eliminar', 'error');
      }
    } catch (error) {
      showToast('Error al eliminar emisor', 'error');
    }
  };

  // ===== FUNCIONES DE BÁSCULA =====
  const cargarPuertosCOM = async () => {
    if (!window.electron?.ipcRenderer) return;
    try {
      const result = await window.electron.ipcRenderer.invoke('scale:list-ports');
      if (result.success) {
        setScalePorts(result.ports);
        // Si no hay puerto seleccionado, usar el guardado o el primero disponible
        if (result.ports.length > 0 && !configuraciones.caja_bascula_puerto) {
          handleChange('caja_bascula_puerto', result.ports[0].path);
        }
      }
    } catch (e) {
      console.error('Error listando puertos COM:', e);
    }
  };

  const handleScaleConnect = async () => {
    const puerto = configuraciones.caja_bascula_puerto;
    const baudRate = parseInt(configuraciones.caja_bascula_baudrate) || 9600;
    if (!puerto) {
      showToast('Selecciona un puerto COM', 'error');
      return;
    }
    setScaleConnecting(true);
    setScaleStatus('');
    try {
      const result = await window.electron.ipcRenderer.invoke('scale:connect', {
        path: puerto,
        baudRate: baudRate
      });
      if (result.success) {
        setScaleConnected(true);
        setScaleStatus(result.message);
        showToast('Báscula conectada', 'success');
      } else {
        setScaleConnected(false);
        setScaleStatus(result.message);
        showToast(result.message, 'error');
      }
    } catch (e) {
      setScaleStatus(e.message);
      showToast('Error conectando báscula', 'error');
    } finally {
      setScaleConnecting(false);
    }
  };

  const handleScaleDisconnect = async () => {
    try {
      const result = await window.electron.ipcRenderer.invoke('scale:disconnect');
      setScaleConnected(false);
      setScaleWeight(null);
      setScaleStatus(result.message || 'Desconectada');
      showToast('Báscula desconectada', 'info');
    } catch (e) {
      showToast('Error desconectando báscula', 'error');
    }
  };

  const handleReadWeight = async () => {
    setReadingWeight(true);
    try {
      const result = await window.electron.ipcRenderer.invoke('scale:read-weight');
      if (result.success) {
        if (result.overload) {
          setScaleWeight({ display: 'SOBRECARGA', stable: false });
        } else {
          setScaleWeight({
            display: `${result.weight} ${result.unit}`,
            stable: result.stable
          });
        }
      } else {
        setScaleWeight({ display: result.message, stable: false });
        showToast(result.message, 'error');
      }
    } catch (e) {
      showToast('Error leyendo peso', 'error');
    } finally {
      setReadingWeight(false);
    }
  };

  // ===== FUNCIONES API NUBE =====
  const handleTestApiNube = async () => {
    const url = (configuraciones.caja_api_nube_url || '').trim();
    if (!url) {
      showToast('Ingresa la URL del API en la nube', 'error');
      return;
    }
    setTestingApiNube(true);
    setApiNubeTestResult(null);
    try {
      const result = await apiService.testApiConnection(url);
      setApiNubeTestResult(result.success ? 'success' : 'error');
      if (result.success) {
        showToast('Conexión exitosa al API Nube', 'success');
      } else {
        showToast(result.message || 'No se pudo conectar al API Nube', 'error');
      }
    } catch (error) {
      setApiNubeTestResult('error');
      showToast('Error probando conexión al API Nube', 'error');
    } finally {
      setTestingApiNube(false);
    }
  };

  // === FUNCIONES DE SINCRONIZACIÓN ===
  const hasSyncConfig = () => {
    return (configuraciones.caja_api_nube_url || '').trim() &&
           (configuraciones.caja_api_nube_negocio || '').trim() &&
           (configuraciones.caja_api_nube_sucursal || '').trim();
  };

  const cargarSyncStatus = async () => {
    if (!hasSyncConfig()) return;
    setLoadingSyncStatus(true);
    try {
      const [statusRes, logRes] = await Promise.all([
        apiService.syncStatus(),
        apiService.syncLog(10)
      ]);
      if (statusRes.success) setSyncStatusData(statusRes.data);
      if (logRes.success) setSyncLog(logRes.data?.logs || []);
    } catch (e) {
      console.error('Error cargando sync status:', e);
    } finally {
      setLoadingSyncStatus(false);
    }
  };

  const handleSyncPush = async () => {
    if (!hasSyncConfig()) {
      showToast('Configura URL, negocio y sucursal primero', 'error');
      return;
    }
    setSyncing(true);
    setSyncOperation('push');
    setSyncResult(null);
    try {
      const result = await apiService.syncPush();
      setSyncResult(result);
      if (result.success) {
        showToast(`Push completado: ${result.data?.resumen?.total_enviados || 0} registros enviados`, 'success');
      } else {
        showToast(result.message || 'Error en push', 'error');
      }
      cargarSyncStatus();
    } catch (e) {
      showToast('Error ejecutando push: ' + e.message, 'error');
    } finally {
      setSyncing(false);
      setSyncOperation('');
    }
  };

  const handleSyncPull = async () => {
    if (!hasSyncConfig()) {
      showToast('Configura URL, negocio y sucursal primero', 'error');
      return;
    }
    setSyncing(true);
    setSyncOperation('pull');
    setSyncResult(null);
    try {
      const result = await apiService.syncPull();
      setSyncResult(result);
      if (result.success) {
        showToast(`Pull completado: ${result.data?.resumen?.total_recibidos || 0} registros recibidos`, 'success');
      } else {
        showToast(result.message || 'Error en pull', 'error');
      }
      cargarSyncStatus();
    } catch (e) {
      showToast('Error ejecutando pull: ' + e.message, 'error');
    } finally {
      setSyncing(false);
      setSyncOperation('');
    }
  };

  const handleSyncFull = async () => {
    if (!hasSyncConfig()) {
      showToast('Configura URL, negocio y sucursal primero', 'error');
      return;
    }
    setSyncing(true);
    setSyncOperation('full');
    setSyncResult(null);
    try {
      const result = await apiService.syncFullSync();
      setSyncResult(result);
      if (result.success) {
        const pushEnv = result.data?.push?.resumen?.total_enviados || 0;
        const pullRec = result.data?.pull?.resumen?.total_recibidos || 0;
        showToast(`Sync completo: ${pushEnv} enviados, ${pullRec} recibidos`, 'success');
      } else {
        showToast(result.message || 'Error en sync', 'error');
      }
      cargarSyncStatus();
    } catch (e) {
      showToast('Error ejecutando sync: ' + e.message, 'error');
    } finally {
      setSyncing(false);
      setSyncOperation('');
    }
  };

  const handleSyncReset = async () => {
    if (!window.confirm('¿Estás seguro? Esto borrará todos los datos de sincronización (local y nube) y permitirá hacer un push inicial limpio.')) return;
    setSyncing(true);
    setSyncOperation('reset');
    try {
      const result = await apiService.syncReset('both');
      if (result.success) {
        showToast('Reset de sync completado', 'success');
        setSyncResult(null);
        cargarSyncStatus();
      } else {
        showToast(result.message || 'Error en reset', 'error');
      }
    } catch (e) {
      showToast('Error en reset: ' + e.message, 'error');
    } finally {
      setSyncing(false);
      setSyncOperation('');
    }
  };

  // Auto-sync: delega al SyncContext global
  const autoSyncEnabled = syncCtx.active || false;
  const autoSyncInterval = syncCtx.interval || 5;
  const autoSyncCountdown = syncCtx.countdown || 0;

  const startAutoSync = () => {
    if (!hasSyncConfig()) {
      showToast('Configura URL, negocio y sucursal primero', 'error');
      return;
    }
    syncCtx.activate(autoSyncInterval);
    showToast(`Sync automático activado cada ${autoSyncInterval} min`, 'success');
  };

  const stopAutoSync = () => {
    syncCtx.deactivate();
    showToast('Sync automático desactivado', 'info');
  };

  // Cargar sync status cuando se muestra el sub-tab sync
  useEffect(() => {
    if (activeTab === 'admin' && adminSubTab === 'sync' && hasSyncConfig()) {
      cargarSyncStatus();
    }
  }, [activeTab, adminSubTab, configuraciones]);

  useEffect(() => {
    if (activeTab === 'caja' && cajaSubTab === 'facturacion') {
      cargarEmisores();
    }
  }, [activeTab, cajaSubTab]);

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatSyncTime = (dateStr) => {
    if (!dateStr) return 'Nunca';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return dateStr; }
  };

  // Cargar puertos COM y verificar estado de báscula cuando se activa el tab Admin
  useEffect(() => {
    if (activeTab === 'admin') {
      cargarPuertosCOM();
      // Verificar si la báscula ya está conectada (auto-conexión al inicio)
      if (window.electron?.ipcRenderer) {
        window.electron.ipcRenderer.invoke('scale:status').then(status => {
          if (status.connected) {
            setScaleConnected(true);
            setScaleStatus(`Conectada a ${status.port} @ ${status.baudRate}`);
          }
        }).catch(() => {});
      }
    }
  }, [activeTab]);

  // Generar HTML de preview para el diseñador de formato
  const generarPreviewFormatoHTML = () => {
    const color = configuraciones.empresa_formato_color || '#1e40af';
    const mostrarLogo = configuraciones.empresa_formato_mostrar_logo !== false && configuraciones.empresa_formato_mostrar_logo !== 'false';
    const infoEmpresa = configuraciones.empresa_formato_info_empresa || '';
    const marcaAgua = configuraciones.empresa_formato_marca_agua !== false && configuraciones.empresa_formato_marca_agua !== 'false';
    const opacidad = (parseFloat(configuraciones.empresa_formato_marca_agua_opacidad) || 5) / 100;
    const mostrarFirma = configuraciones.empresa_formato_mostrar_firma === true || configuraciones.empresa_formato_mostrar_firma === 'true';
    const desgloseIva = configuraciones.empresa_formato_desglose_iva === true || configuraciones.empresa_formato_desglose_iva === 'true';
    const marcaAguaTamano = parseInt(configuraciones.empresa_formato_marca_agua_tamano) || 500;
    const logoTamano = parseInt(configuraciones.empresa_formato_logo_tamano) || 100;
    const infoFontSize = parseInt(configuraciones.empresa_formato_info_tamano) || 9;
    const clienteFontSize = parseInt(configuraciones.empresa_formato_cliente_tamano) || 10;
    const copiaOriginal = configuraciones.empresa_formato_copia_original !== false && configuraciones.empresa_formato_copia_original !== 'false';
    const copiaCliente = configuraciones.empresa_formato_copia_cliente === true || configuraciones.empresa_formato_copia_cliente === 'true';
    const copiaMostrador = configuraciones.empresa_formato_copia_mostrador === true || configuraciones.empresa_formato_copia_mostrador === 'true';
    const piePagina = configuraciones.empresa_formato_pie_pagina || '';
    const mostrarPagare = configuraciones.empresa_formato_pagare === true || configuraciones.empresa_formato_pagare === 'true';
    const pagareTexto = configuraciones.empresa_formato_pagare_texto || 'POR EL PRESENTE PAGARÉ RECONOZCO(EMOS) DEBER Y ME(NOS) OBLIGO(AMOS) INCONDICIONAL A PAGAR A LA ORDEN DE {empresa} EN ESTA CIUDAD DE {ciudad}, LA SUMA QUE AMPARA ESTE PAGARÉ. LA CANTIDAD QUE SE ORIGINA EN SATISFACCIÓN CAUSARÁ INTERESES MORATORIOS DE 6% MENSUAL. DESPUÉS DE SU VENCIMIENTO, EN CASO DE COBRO JUDICIAL PAGARÉ LOS GASTOS QUE SE ORIGINEN SOMETIÉNDOME A LOS TRIBUNALES QUE EL ACREEDOR ELIJA Y RENUNCIANDO AL FUERO DE MI ACTUAL O FUTURO DOMICILIO. ESTE PAGARÉ ES MERCANTIL Y ESTÁ REGIDO POR LA LEY GENERAL DE TÍTULOS Y OPERACIONES DE CRÉDITO EN SU ART. 173 PARTE FINAL Y ARTS. CORRELATIVOS POR NO SER PAGARÉ DOMICILIADO.';
    const logo = configuraciones.empresa_logo || '';
    const nombreEmpresa = configuraciones.empresa_nombre || 'Mi Empresa';

    const productosEjemplo = [
      { clave: '7501001', cantidad: 2, unidad: 'PZ', nombre: 'Aceite Vegetal 1L', precio: 45.50, importe: 91.00 },
      { clave: '7502034', cantidad: 1, unidad: 'KG', nombre: 'Frijol Bayo', precio: 38.00, importe: 38.00 },
      { clave: '7503012', cantidad: 3, unidad: 'PZ', nombre: 'Leche Entera 1L', precio: 28.50, importe: 85.50 },
      { clave: '7504056', cantidad: 1, unidad: 'PZ', nombre: 'Arroz Premium 900g', precio: 32.00, importe: 32.00 },
      { clave: '7505078', cantidad: 2, unidad: 'PZ', nombre: 'Atún en Aceite 170g', precio: 22.50, importe: 45.00 },
    ];
    const subtotal = productosEjemplo.reduce((s, p) => s + p.importe, 0);

    const productosHTML = productosEjemplo.map(p => `
      <tr>
        <td style="padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${p.clave}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${p.nombre}</td>
        <td style="text-align:center;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${p.cantidad} ${p.unidad}</td>
        <td style="text-align:right;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${formatMoney(p.precio)}</td>
        <td style="text-align:right;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${formatMoney(p.importe)}</td>
      </tr>`).join('');

    // Copias activas
    const copias = [];
    if (copiaOriginal) copias.push('ORIGINAL');
    if (copiaCliente) copias.push('COPIA CLIENTE');
    if (copiaMostrador) copias.push('COPIA MOSTRADOR');
    if (copias.length === 0) copias.push(''); // al menos una página sin etiqueta

    const generarPagina = (etiqueta) => `
      <div style="position:relative;display:flex;flex-direction:column;min-height:calc(100vh - 40px);page-break-after:always;">
        ${marcaAgua && logo ? `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);opacity:${opacidad};pointer-events:none;z-index:0;">
          <img src="${logo}" style="width:${marcaAguaTamano}px;height:${marcaAguaTamano}px;object-fit:contain;" />
        </div>` : ''}
        <div style="flex:1;position:relative;z-index:1;">
          ${etiqueta ? `<div style="text-align:right;margin-bottom:4px;"><span style="font-size:8px;font-weight:700;color:${color};border:1px solid ${color};padding:2px 8px;border-radius:3px;text-transform:uppercase;">${etiqueta}</span></div>` : ''}
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:12px;border-bottom:2px solid ${color};">
            <div style="display:flex;align-items:center;gap:12px;">
              ${mostrarLogo && logo ? `<img src="${logo}" style="max-width:${logoTamano}px;max-height:${Math.round(logoTamano * 0.6)}px;object-fit:contain;" />` : ''}
              <div>
                <div style="font-size:14px;font-weight:700;color:${color};">${nombreEmpresa}</div>
                ${infoEmpresa ? `<div style="font-size:${infoFontSize}px;color:#6b7280;max-width:250px;margin-top:2px;">${infoEmpresa}</div>` : ''}
              </div>
            </div>
            <div style="display:flex;gap:10px;">
              <div style="border:2px solid ${color};border-radius:4px;min-width:120px;">
                <div style="background:${color};color:white;padding:3px 10px;font-size:9px;font-weight:600;text-transform:uppercase;">Vendedor</div>
                <div style="padding:5px 10px;font-size:11px;font-weight:500;">Juan Pérez</div>
              </div>
              <div style="border:2px solid ${color};border-radius:4px;min-width:100px;">
                <div style="background:${color};color:white;padding:3px 10px;font-size:9px;font-weight:600;text-transform:uppercase;">Folio</div>
                <div style="padding:5px 10px;font-size:11px;font-weight:500;">#00123</div>
              </div>
            </div>
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:10px 14px;margin-bottom:14px;">
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px 16px;">
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Cliente</span><div style="font-size:${clienteFontSize}px;font-weight:600;">María García López</div></div>
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Teléfono</span><div style="font-size:${clienteFontSize}px;">644-123-4567</div></div>
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Fecha</span><div style="font-size:${clienteFontSize}px;">04/03/2026 14:30</div></div>
              <div style="grid-column:span 2;"><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Domicilio</span><div style="font-size:${clienteFontSize}px;">Calle Ejemplo #123, Col. Centro, Cd. Obregón, Son.</div></div>
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Condiciones de pago</span><div style="font-size:${clienteFontSize}px;">Crédito</div></div>
              <div style="grid-column:span 3;"><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Observaciones</span><div style="font-size:${clienteFontSize}px;">Entregar por la mañana antes de las 10am</div></div>
            </div>
          </div>
          <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
            <thead><tr style="background:${color};">
              <th style="color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase;font-weight:600;width:70px;">Clave</th>
              <th style="color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase;font-weight:600;">Artículo</th>
              <th style="color:white;padding:5px 6px;text-align:center;font-size:9px;text-transform:uppercase;font-weight:600;width:60px;">Unidad</th>
              <th style="color:white;padding:5px 6px;text-align:right;font-size:9px;text-transform:uppercase;font-weight:600;width:70px;">Precio</th>
              <th style="color:white;padding:5px 6px;text-align:right;font-size:9px;text-transform:uppercase;font-weight:600;width:80px;">Importe</th>
            </tr></thead>
            <tbody>${productosHTML}</tbody>
          </table>
          <div style="text-align:center;font-size:10px;font-weight:600;color:${color};margin-bottom:12px;">ENTREGA A DOMICILIO &bull; CRÉDITO</div>
        </div>
        <div style="margin-top:auto;position:relative;z-index:1;">
          <div style="display:flex;justify-content:flex-end;margin-bottom:12px;">
            <div style="border:1.5px solid #374151;min-width:220px;">
              <div style="display:flex;border-bottom:1px solid #374151;">
                <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                  <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Subtotal</div>
                  <div style="font-size:10px;font-weight:500;text-align:right;">${desgloseIva ? formatMoney(subtotal / 1.16) : formatMoney(subtotal)}</div>
                </div>
                <div style="flex:1;padding:4px 8px;">
                  <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">IVA ${desgloseIva ? '16%' : 'al 0%'}</div>
                  <div style="font-size:10px;font-weight:500;text-align:right;">${desgloseIva ? formatMoney(subtotal - subtotal / 1.16) : formatMoney(0)}</div>
                </div>
              </div>
              <div style="display:flex;background:${color};">
                <div style="flex:1;padding:6px 8px;">
                  <div style="font-size:7px;color:rgba(255,255,255,0.8);text-transform:uppercase;font-weight:600;">Total (MXN)</div>
                  <div style="font-size:14px;font-weight:700;color:white;text-align:right;">${formatMoney(subtotal)}</div>
                </div>
              </div>
            </div>
          </div>
          ${mostrarPagare ? `
          <div style="border:1.5px solid #374151;margin:12px 0;page-break-inside:avoid;">
            <div style="display:flex;border-bottom:1px solid #374151;">
              <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Pagaré No.</div>
                <div style="font-size:9px;font-weight:700;">ÚNICO</div>
              </div>
              <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Emisión</div>
                <div style="font-size:9px;">04/03/2026</div>
              </div>
              <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Importe</div>
                <div style="font-size:9px;font-weight:700;">${formatMoney(subtotal)}</div>
              </div>
              <div style="flex:1;padding:4px 8px;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Folio</div>
                <div style="font-size:9px;font-weight:700;">#00123</div>
              </div>
            </div>
            <div style="padding:6px 8px;border-bottom:1px solid #374151;">
              <p style="font-size:7px;line-height:1.4;color:#374151;text-align:justify;">${pagareTexto
                .replace(/\{ciudad\}/g, 'Cd. Obregón, Son.')
                .replace(/\{fecha_emision\}/g, '04/03/2026')
                .replace(/\{empresa\}/g, nombreEmpresa)
                .replace(/\{importe\}/g, formatMoney(subtotal))
                .replace(/\{importe_letra\}/g, '(Doscientos noventa y un pesos 50/100 m.n.)')
                .replace(/\{fecha_vencimiento\}/g, '04/04/2026')
              }</p>
            </div>
            <div style="display:flex;">
              <div style="flex:1;padding:6px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Otorgante</div>
                <div style="font-size:8px;">${nombreEmpresa}</div>
              </div>
              <div style="flex:1;padding:6px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Vencimiento</div>
                <div style="font-size:8px;">04/04/2026</div>
              </div>
              <div style="flex:1;padding:6px 8px;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Domicilio</div>
                <div style="font-size:8px;">Cd. Obregón, Son.</div>
              </div>
            </div>
            <div style="display:flex;border-top:1px solid #374151;">
              <div style="flex:1;padding:8px;border-right:1px solid #374151;text-align:center;">
                <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                <div style="font-size:7px;color:#6b7280;">Nombre</div>
              </div>
              <div style="flex:1;padding:8px;border-right:1px solid #374151;text-align:center;">
                <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                <div style="font-size:7px;color:#6b7280;">Firma</div>
              </div>
              <div style="flex:1;padding:8px;text-align:center;">
                <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                <div style="font-size:7px;color:#6b7280;">Persona autorizada a recibir</div>
              </div>
            </div>
          </div>
          ` : ''}
          <div style="text-align:center;font-size:8px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
            ${piePagina ? `<p style="margin-bottom:4px;">${piePagina}</p>` : ''}
            <p>Impreso el 04/03/2026 14:30 | Usuario: Juan Pérez</p>
          </div>
        </div>
      </div>`;

    return `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <style>
        @page{size:letter;margin:0.8cm;}
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;line-height:1.4;color:#1f2937;padding:20px 24px;}
      </style></head><body>
      ${copias.map(etiqueta => generarPagina(etiqueta)).join('')}
      </body></html>`;
  };

  // ===== NÚMERO A LETRA (ESPAÑOL) =====
  const numeroALetra = (num) => {
    const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE'];
    const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    const convertirGrupo = (n) => {
      if (n === 0) return '';
      if (n === 100) return 'CIEN';
      let resultado = '';
      if (n >= 100) { resultado += centenas[Math.floor(n / 100)] + ' '; n %= 100; }
      if (n >= 10 && n <= 15) return resultado + especiales[n - 10];
      if (n >= 16 && n <= 19) return resultado + 'DIECI' + unidades[n - 10];
      if (n >= 21 && n <= 29) return resultado + 'VEINTI' + unidades[n - 20];
      if (n >= 10) { resultado += decenas[Math.floor(n / 10)]; n %= 10; if (n > 0) resultado += ' Y '; }
      resultado += unidades[n];
      return resultado.trim();
    };

    const entero = Math.floor(Math.abs(num));
    const centavos = Math.round((Math.abs(num) - entero) * 100);

    if (entero === 0) return `CERO PESOS ${String(centavos).padStart(2, '0')}/100 M.N.`;

    let texto = '';
    if (entero >= 1000000) {
      const millones = Math.floor(entero / 1000000);
      texto += (millones === 1 ? 'UN MILLON' : convertirGrupo(millones) + ' MILLONES') + ' ';
    }
    const resto = entero % 1000000;
    if (resto >= 1000) {
      const miles = Math.floor(resto / 1000);
      texto += (miles === 1 ? 'MIL' : convertirGrupo(miles) + ' MIL') + ' ';
    }
    const unidad = resto % 1000;
    if (unidad > 0) texto += convertirGrupo(unidad);

    return `${texto.trim()} PESOS ${String(centavos).padStart(2, '0')}/100 M.N.`;
  };

  // ===== PREVIEW DEL TICKET =====
  const generarPreviewTicketHTML = () => {
    const logo = configuraciones.empresa_logo || '';
    const mostrarLogo = configuraciones.impresion_mostrar_logo === true || configuraciones.impresion_mostrar_logo === 'true';
    const encabezado = configuraciones.impresion_encabezado_ticket || '';
    const pie = configuraciones.impresion_pie_ticket || '';
    const codigoBarras = configuraciones.impresion_codigo_barras !== false && configuraciones.impresion_codigo_barras !== 'false';
    const desgloseIva = configuraciones.impresion_desglose_iva === true || configuraciones.impresion_desglose_iva === 'true';
    const totalEnLetra = configuraciones.impresion_total_en_letra === true || configuraciones.impresion_total_en_letra === 'true';
    const leyendaFactura = configuraciones.impresion_leyenda_factura || '';
    const portalFacturacionUrl = configuraciones.impresion_portal_facturacion_url || '';
    const mostrarQrPortal = (configuraciones.impresion_mostrar_qr_portal === true || configuraciones.impresion_mostrar_qr_portal === 'true') && portalFacturacionUrl;
    const margenIzquierdo = configuraciones.impresion_margen_izquierdo ?? 4;
    const sucursalNombre = configuraciones.caja_api_nube_sucursal || '';
    const codigoEjemplo = 'A3F7B2C1';

    // Construir URL del QR con params
    let qrPreviewUrl = portalFacturacionUrl;
    if (portalFacturacionUrl) {
      const sep = portalFacturacionUrl.includes('?') ? '&' : '?';
      qrPreviewUrl = portalFacturacionUrl + sep + 'codigo=' + codigoEjemplo;
      if (sucursalNombre) {
        qrPreviewUrl += '&sucursal=' + encodeURIComponent(sucursalNombre);
      }
    }

    const W = 36;
    const LINE = '='.repeat(W);
    const DASH = '-'.repeat(W);
    const folio = 'VTA-20260304-0001';

    // Replicar funciones PHP del API
    const centrar = (txt) => {
      if (txt.length >= W) return txt;
      const pad = Math.floor((W - txt.length) / 2);
      return ' '.repeat(pad) + txt;
    };
    const lineaMonto = (label, monto) => {
      const montoStr = '$' + monto.toFixed(2);
      let espacios = W - label.length - montoStr.length;
      if (espacios < 1) espacios = 1;
      return label + ' '.repeat(espacios) + montoStr;
    };

    // Productos de ejemplo (mismo formato que VentasMostrador.php)
    const productos = [
      { codigo: '388_KG', nombre: 'BOLSA 50*70 CALIBRE 300', cant: 5, unidad: 'KG', precio: 52.79 },
      { codigo: '0150_PQT', nombre: 'CHAROLA # 8SS LEON 10/50 (PQT)', cant: 1, unidad: 'PQT', precio: 34.48 },
      { codigo: '255_PZA', nombre: 'CONC. LIMA 120ML C/30', cant: 1, unidad: 'PZ', precio: 38.21 },
      { codigo: '3_PZA', nombre: 'JABON LIRIO 14/150GR', cant: 3, unidad: 'PZ', precio: 200.99 },
      { codigo: '02652_PZA', nombre: 'CLORALEX LIMPIADOR 10LT', cant: 1, unidad: 'PZ', precio: 139.15 },
    ];

    // Header de productos alineado con datos
    const hIzq = '  CANT/UNID';
    const hCentro = 'P.U';
    const hDer = 'IMPORTE';
    let hEspacio = W - hIzq.length - hCentro.length - hDer.length;
    if (hEspacio < 2) hEspacio = 2;
    const hEspIzq = Math.floor(hEspacio / 2);
    const hEspDer = hEspacio - hEspIzq;
    const productosHeader = hIzq + ' '.repeat(hEspIzq) + hCentro + ' '.repeat(hEspDer) + hDer;

    let productosTexto = '';
    productos.forEach(p => {
      const subtotal = p.cant * p.precio;
      // Línea 1: [codigo] nombre
      let l1 = '[' + p.codigo + '] ';
      const espacioDesc = W - l1.length;
      let nombre = p.nombre;
      if (nombre.length > espacioDesc) nombre = nombre.substring(0, espacioDesc - 1) + '.';
      l1 += nombre;
      // Línea 2: cant unidad | precio | subtotal
      const izq = '  ' + p.cant + ' ' + p.unidad;
      const centro = '$' + p.precio.toFixed(2);
      const der = '$' + subtotal.toFixed(2);
      let espacioTotal = W - izq.length - centro.length - der.length;
      if (espacioTotal < 2) espacioTotal = 2;
      const espIzq = Math.floor(espacioTotal / 2);
      const espDer = espacioTotal - espIzq;
      let l2 = izq + ' '.repeat(espIzq) + centro + ' '.repeat(espDer) + der;
      if (l2.length > W) l2 = l2.substring(0, W);
      productosTexto += l1 + '\n' + l2 + '\n';
    });

    const total = productos.reduce((s, p) => s + p.cant * p.precio, 0);

    const ticketTexto = `${LINE}
${centrar('VENTA DE MOSTRADOR')}
${LINE}
Folio: ${folio}
Fecha: 04/03/2026 14:30
Cliente: Juan Pérez López
Atendió: admin
${LINE}
${productosHeader}
${DASH}
${productosTexto}${DASH}
${desgloseIva ? `
${lineaMonto('Subtotal:', total / 1.16)}
${lineaMonto('IVA (16%):', total - total / 1.16)}` : `
${lineaMonto('Subtotal:', total)}`}
${LINE}
${lineaMonto('TOTAL:', total)}
${LINE}${totalEnLetra ? `
${numeroALetra(total)}` : ''}
Método de Pago: EFECTIVO`;

    return `<!DOCTYPE html><html><head><meta charset="utf-8">
      <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128+Text&display=swap" rel="stylesheet">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Courier New', Courier, monospace;
          font-size: 12.5px;
          font-weight: bold;
          color: #000000;
          margin: 0;
          padding: 1mm 0mm 1mm ${margenIzquierdo}mm;
          white-space: pre-wrap;
          background: white;
          line-height: 1.4;
          -webkit-font-smoothing: none;
          text-rendering: geometricPrecision;
          overflow: hidden;
        }
        .ticket-logo { text-align: center; margin-bottom: 2mm; white-space: normal; max-width: 36ch; }
        .ticket-logo img { display: block; margin: 0 auto; max-height: 100px; max-width: 250px; image-rendering: -webkit-optimize-contrast; }
        .barcode-container { text-align: center; margin: 5mm 0; white-space: normal; max-width: 36ch; }
        .barcode { font-family: 'Libre Barcode 128 Text', cursive; font-size: 48px; color: #000; line-height: 1; }
        .barcode-text { font-family: 'Courier New', monospace; font-size: 10px; margin-top: 2mm; }
        .leyenda-factura { text-align: center; margin: 3mm auto 0 auto; white-space: pre-wrap; max-width: 36ch; font-size: 11px; }
        .qr-portal { text-align: center; margin-top: 2mm; white-space: normal; max-width: 36ch; }
        .qr-portal img { display: block; margin: 0 auto; width: 150px; height: 150px; image-rendering: pixelated; }
      </style></head>
      <body>${mostrarLogo && logo ? `<div class="ticket-logo"><img src="${logo}" alt="Logo" /></div>` : ''}${encabezado ? `<div style="text-align:center;margin-bottom:3mm;white-space:pre-wrap;max-width:36ch;">${encabezado}</div>` : ''}${ticketTexto}
${pie ? `<div style="text-align:center;margin-top:3mm;white-space:pre-wrap;max-width:36ch;">${pie}</div>` : ''}${codigoBarras ? `<div class="barcode-container">
<div class="barcode">${folio}</div>
<div class="barcode-text">${folio}</div>
</div>` : ''}${leyendaFactura ? `<div class="leyenda-factura">${leyendaFactura}</div>` : ''}${mostrarQrPortal ? `<div class="qr-portal">
<img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrPreviewUrl)}" alt="QR Portal" />
</div>` : ''}${mostrarQrPortal ? `<div style="text-align:center;margin-top:2mm;white-space:normal;max-width:36ch;font-size:12px;font-weight:bold;">Código: ${codigoEjemplo}</div>` : ''}${sucursalNombre ? `<div style="text-align:center;margin-top:1mm;white-space:normal;max-width:36ch;font-size:11px;">Sucursal: ${sucursalNombre}</div>` : ''}
      </body></html>`;
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">
          <Settings size={24} />
          <h1>Configuraciones</h1>
        </div>
        {hasChanges && (
          <div className="settings-actions">
            <Button
              variant="outline"
              onClick={handleDescartar}
              disabled={saving}
            >
              Descartar
            </Button>
            <Button
              variant="primary"
              onClick={handleGuardar}
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        {canViewCaja && (
          <button
            className={`settings-tab ${activeTab === 'caja' ? 'active' : ''}`}
            onClick={() => setActiveTab('caja')}
          >
            <Monitor size={16} />
            Configuración de Caja
          </button>
        )}
        {canViewAdmin && (
          <button
            className={`settings-tab ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            <ShieldCheck size={16} />
            Administración
          </button>
        )}
      </div>

      <div className="settings-content">

        {/* ====== TAB: CONFIGURACIÓN DE CAJA ====== */}
        {activeTab === 'caja' && canViewCaja && (
          <div>
            {/* Sub-tabs de Caja */}
            <div className="settings-subtabs">
              <button
                className={`settings-subtab ${cajaSubTab === 'configuracion' ? 'active' : ''}`}
                onClick={() => setCajaSubTab('configuracion')}
              >
                <Settings size={15} />
                Configuración
              </button>
              <button
                className={`settings-subtab ${cajaSubTab === 'formato' ? 'active' : ''}`}
                onClick={() => setCajaSubTab('formato')}
              >
                <Palette size={15} />
                Diseño Formato
              </button>
              <button
                className={`settings-subtab ${cajaSubTab === 'ticket' ? 'active' : ''}`}
                onClick={() => setCajaSubTab('ticket')}
              >
                <Receipt size={15} />
                Diseño Ticket
              </button>
              {canConfigFacturacion && (
              <button
                className={`settings-subtab ${cajaSubTab === 'facturacion' ? 'active' : ''}`}
                onClick={() => setCajaSubTab('facturacion')}
              >
                <FileText size={15} />
                Facturación
              </button>
              )}
            </div>

            {/* Sub-tab: Configuración (contenido original) */}
            {cajaSubTab === 'configuracion' && (
          <div className="settings-three-columns">
            {/* Columna 1 */}
            <div className="settings-column">
              {/* Sección de Empresa (Logo y Comisión tarjeta) */}
              <div className="settings-section">
                <div className="section-header">
                  <Building size={20} />
                  <h2>Empresa</h2>
                </div>

                <div className="settings-card">
                  <div className="setting-item-compact global-setting">
                    <div className="global-badge"><Globe size={10} /> Global</div>
                    <div className="setting-row logo-row">
                      <span>Logo de la empresa</span>
                      <div className="logo-upload-container">
                        {configuraciones.empresa_logo ? (
                          <div className="logo-preview-wrapper">
                            <img
                              src={configuraciones.empresa_logo}
                              alt="Logo"
                              className="logo-preview"
                            />
                            <button
                              type="button"
                              className="logo-remove-btn"
                              onClick={handleRemoveLogo}
                              title="Eliminar logo"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ) : (
                          <div
                            className="logo-upload-area"
                            onClick={() => logoInputRef.current?.click()}
                          >
                            <Upload size={20} />
                            <span>Subir logo</span>
                          </div>
                        )}
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={handleLogoChange}
                          style={{ display: 'none' }}
                        />
                        {configuraciones.empresa_logo && (
                          <button
                            type="button"
                            className="logo-change-btn"
                            onClick={() => logoInputRef.current?.click()}
                          >
                            Cambiar
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="logo-hint">
                      Formatos: JPG, PNG. Tamaño máximo: 500KB
                    </div>
                  </div>

                  <div className="setting-item-compact global-setting">
                    <div className="global-badge"><Globe size={10} /> Global</div>
                    <div className="setting-row">
                      <span>Comisión por pago con tarjeta (%)</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          className="number-input compact"
                          placeholder="0"
                          min="0"
                          max="100"
                          step="0.5"
                          value={configuraciones.empresa_comision_tarjeta || ''}
                          onChange={(e) => handleChange('empresa_comision_tarjeta', parseFloat(e.target.value) || 0)}
                          style={{ width: '80px' }}
                        />
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>%</span>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Porcentaje de comisión a agregar cuando el cliente paga con tarjeta y tiene productos con precio mayoreo (precio 2 o superior).
                    </div>
                  </div>

                  <div className="setting-item-compact global-setting">
                    <div className="global-badge"><Globe size={10} /> Global</div>
                    <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                      <span>Comisiones por envío a domicilio (%)</span>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ color: '#374151', fontSize: '13px', fontWeight: 500 }}>Envío 1:</span>
                          <input
                            type="number"
                            className="number-input compact"
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.5"
                            value={configuraciones.empresa_comision_envio_1 || ''}
                            onChange={(e) => handleChange('empresa_comision_envio_1', parseFloat(e.target.value) || 0)}
                            style={{ width: '80px' }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '14px' }}>%</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ color: '#374151', fontSize: '13px', fontWeight: 500 }}>Envío 2:</span>
                          <input
                            type="number"
                            className="number-input compact"
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.5"
                            value={configuraciones.empresa_comision_envio_2 || ''}
                            onChange={(e) => handleChange('empresa_comision_envio_2', parseFloat(e.target.value) || 0)}
                            style={{ width: '80px' }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '14px' }}>%</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ color: '#374151', fontSize: '13px', fontWeight: 500 }}>Envío 3:</span>
                          <input
                            type="number"
                            className="number-input compact"
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.5"
                            value={configuraciones.empresa_comision_envio_3 || ''}
                            onChange={(e) => handleChange('empresa_comision_envio_3', parseFloat(e.target.value) || 0)}
                            style={{ width: '80px' }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '14px' }}>%</span>
                        </div>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Porcentajes disponibles para cobrar por envío a domicilio. Dejar en 0 los que no se usen.
                    </div>
                  </div>

                  <div className="setting-item-compact global-setting">
                    <div className="global-badge"><Globe size={10} /> Global</div>
                    <div className="setting-row">
                      <span>Campo de costo a utilizar</span>
                      <div className="ubicacion-selector compact">
                        <button
                          className={`ubicacion-btn ${(!configuraciones.empresa_campo_costo || configuraciones.empresa_campo_costo === 'precio_compra') ? 'active' : ''}`}
                          onClick={() => handleChange('empresa_campo_costo', 'precio_compra')}
                        >
                          Precio Compra
                        </button>
                        <button
                          className={`ubicacion-btn ${configuraciones.empresa_campo_costo === 'costo_precios' ? 'active' : ''}`}
                          onClick={() => handleChange('empresa_campo_costo', 'costo_precios')}
                        >
                          Costo Precios
                        </button>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Define qué campo de costo se muestra en los reportes y otros componentes. "Precio Compra" es el costo real.
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Columna 2 */}
            <div className="settings-column">
              {/* Sección de Punto de Venta */}
              <div className="settings-section">
                <div className="section-header">
                  <Monitor size={20} />
                  <h2>Punto de Venta</h2>
                </div>

                <div className="settings-card">
                  <div className="setting-item-compact">
                    <div className="setting-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertTriangle size={16} color="#f59e0b" />
                        <span>Alerta de efectivo máximo</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>$</span>
                        <input
                          type="number"
                          className="number-input compact"
                          placeholder="0"
                          min="0"
                          step="100"
                          value={configuraciones.caja_alerta_efectivo_maximo || ''}
                          onChange={(e) => handleChange('caja_alerta_efectivo_maximo', parseFloat(e.target.value) || 0)}
                          style={{ width: '120px' }}
                        />
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Se mostrará una alerta cuando el efectivo en caja supere este monto. Dejar en 0 para desactivar.
                    </div>
                  </div>

                  <div className="setting-item-compact">
                    <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                      <span>Unidades que abren el modal "¿Cuántos?"</span>
                      <input
                        type="text"
                        className="text-input-compact"
                        placeholder="Ej: kg, kilo, kilos, gramo, litro"
                        value={configuraciones.caja_unidades_modal_cantidad || ''}
                        onChange={(e) => handleChange('caja_unidades_modal_cantidad', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Unidades separadas por coma. Solo los productos con estas unidades abrirán el modal de cantidad. Dejar vacío para nunca preguntar.
                    </div>
                  </div>

                  <div className="setting-item-compact">
                    <div className="setting-row">
                      <span>Día de entrega de pedidos</span>
                      <div className="ubicacion-selector compact">
                        <button
                          className={`ubicacion-btn ${(!configuraciones.caja_dia_entrega_pedido || String(configuraciones.caja_dia_entrega_pedido) === '0') ? 'active' : ''}`}
                          onClick={() => handleChange('caja_dia_entrega_pedido', '0')}
                        >
                          Hoy
                        </button>
                        <button
                          className={`ubicacion-btn ${String(configuraciones.caja_dia_entrega_pedido) === '1' ? 'active' : ''}`}
                          onClick={() => handleChange('caja_dia_entrega_pedido', '1')}
                        >
                          Mañana
                        </button>
                        <button
                          className={`ubicacion-btn ${String(configuraciones.caja_dia_entrega_pedido) === '2' ? 'active' : ''}`}
                          onClick={() => handleChange('caja_dia_entrega_pedido', '2')}
                        >
                          Pasado mañana
                        </button>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Día de entrega que se asigna por defecto al crear un pedido nuevo.
                    </div>
                  </div>

                  <div className="setting-item-compact">
                    <div className="setting-row">
                      <span>Impresora predeterminada</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select
                          className="text-input-compact"
                          value={configuraciones.impresion_impresora_predeterminada || ''}
                          onChange={(e) => handleChange('impresion_impresora_predeterminada', e.target.value)}
                          style={{ minWidth: '180px' }}
                          disabled={loadingImpresoras}
                        >
                          <option value="">Del sistema</option>
                          {impresoras.map((printer) => (
                            <option key={printer.name || printer} value={printer.name || printer}>
                              {printer.displayName || printer.name || printer}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={cargarImpresoras}
                          disabled={loadingImpresoras}
                          style={{
                            background: 'none',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            padding: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Actualizar lista"
                        >
                          <RefreshCw size={14} className={loadingImpresoras ? 'spinning' : ''} />
                        </button>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Impresora donde se envían los tickets. "Del sistema" usa la impresora predeterminada del equipo.
                    </div>
                  </div>

                  <div className="setting-item-compact">
                    <div className="setting-row">
                      <span>Imprimir pedido</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.caja_imprimir_pedido !== false && configuraciones.caja_imprimir_pedido !== 'false'}
                          onChange={(e) => handleChange('caja_imprimir_pedido', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Si está activado, el pedido se imprimirá directamente al crearse.
                    </div>
                  </div>

                  <div className={`setting-item-compact ${configuraciones.caja_imprimir_pedido === false || configuraciones.caja_imprimir_pedido === 'false' ? 'disabled' : ''}`}>
                    <div className="setting-row">
                      <span>Tipo de impresión de pedidos</span>
                      <div className="ubicacion-selector compact">
                        <button
                          className={`ubicacion-btn ${(!configuraciones.caja_tipo_impresion_pedido || configuraciones.caja_tipo_impresion_pedido === 'ticket') ? 'active' : ''}`}
                          onClick={() => handleChange('caja_tipo_impresion_pedido', 'ticket')}
                          disabled={configuraciones.caja_imprimir_pedido === false || configuraciones.caja_imprimir_pedido === 'false'}
                        >
                          Ticket
                        </button>
                        <button
                          className={`ubicacion-btn ${configuraciones.caja_tipo_impresion_pedido === 'formato' ? 'active' : ''}`}
                          onClick={() => handleChange('caja_tipo_impresion_pedido', 'formato')}
                          disabled={configuraciones.caja_imprimir_pedido === false || configuraciones.caja_imprimir_pedido === 'false'}
                        >
                          Formato
                        </button>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      "Ticket" imprime en rollo térmico (80mm). "Formato" imprime en hoja carta/A4.
                    </div>
                  </div>

                  <div className={`setting-item-compact ${configuraciones.caja_imprimir_pedido === false || configuraciones.caja_imprimir_pedido === 'false' ? 'disabled' : ''}`}>
                    <div className="setting-row">
                      <span>Impresora para pedidos</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select
                          className="text-input-compact"
                          value={configuraciones.caja_impresora_pedidos || ''}
                          onChange={(e) => handleChange('caja_impresora_pedidos', e.target.value)}
                          style={{ minWidth: '180px' }}
                          disabled={loadingImpresoras || configuraciones.caja_imprimir_pedido === false || configuraciones.caja_imprimir_pedido === 'false'}
                        >
                          <option value="">Misma que tickets</option>
                          {impresoras.map((printer) => (
                            <option key={printer.name || printer} value={printer.name || printer}>
                              {printer.displayName || printer.name || printer}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={cargarImpresoras}
                          disabled={loadingImpresoras}
                          style={{
                            background: 'none',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            padding: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Actualizar lista"
                        >
                          <RefreshCw size={14} className={loadingImpresoras ? 'spinning' : ''} />
                        </button>
                      </div>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Impresora donde se envían los pedidos. "Misma que tickets" usa la impresora predeterminada configurada arriba.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna 3 */}
            <div className="settings-column">
              {/* Sección de Impresión */}
              <div className="settings-section">
                <div className="section-header">
                  <Printer size={20} />
                  <h2>Configuraciones de Impresión</h2>
                </div>

                <div className="settings-card">
                  <div className="setting-item-compact">
                    <div className="setting-row">
                      <span>Imprimir comanda</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.impresion_imprimir_comanda === true}
                          onChange={(e) => handleChange('impresion_imprimir_comanda', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Si está activado, se imprimirá la comanda automáticamente al registrar una venta.
                    </div>
                  </div>

                  <div className={`setting-item-compact ${!configuraciones.impresion_imprimir_comanda ? 'disabled' : ''}`}>
                    <div className="setting-row">
                      <span>Preguntar ubicación al imprimir comanda</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.impresion_preguntar_ubicacion === true}
                          onChange={(e) => handleChange('impresion_preguntar_ubicacion', e.target.checked)}
                          disabled={!configuraciones.impresion_imprimir_comanda}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  <div className={`setting-item-compact ${!configuraciones.impresion_imprimir_comanda || configuraciones.impresion_preguntar_ubicacion ? 'disabled' : ''}`}>
                    <div className="setting-row">
                      <span>Ubicación predeterminada</span>
                      <div className="ubicacion-selector compact">
                        <button
                          className={`ubicacion-btn ${configuraciones.impresion_ubicacion_default === 'local' ? 'active' : ''}`}
                          onClick={() => handleChange('impresion_ubicacion_default', 'local')}
                          disabled={!configuraciones.impresion_imprimir_comanda || configuraciones.impresion_preguntar_ubicacion === true}
                        >
                          Local
                        </button>
                        <button
                          className={`ubicacion-btn ${configuraciones.impresion_ubicacion_default === 'almacen' ? 'active' : ''}`}
                          onClick={() => handleChange('impresion_ubicacion_default', 'almacen')}
                          disabled={!configuraciones.impresion_imprimir_comanda || configuraciones.impresion_preguntar_ubicacion === true}
                        >
                          Almacenes
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
            )}

            {/* Sub-tab: Diseño Formato */}
            {cajaSubTab === 'formato' && (
              <div className="formato-designer">
                <div className="formato-designer-left">
                {/* Columna 1: Encabezado y marca de agua */}
                <div className="formato-controls">
                  <div className="formato-control-group">
                    <label>Color principal</label>
                    <div className="color-picker-row">
                      <input
                        type="color"
                        value={configuraciones.empresa_formato_color || '#1e40af'}
                        onChange={(e) => handleChange('empresa_formato_color', e.target.value)}
                      />
                      <span className="color-value">{configuraciones.empresa_formato_color || '#1e40af'}</span>
                    </div>
                  </div>

                  <div className="formato-control-group">
                    <label>Nombre de la empresa</label>
                    <input
                      type="text"
                      className="text-input-compact"
                      placeholder="Ej: Abarrotes Don Juan"
                      value={configuraciones.empresa_nombre || ''}
                      onChange={(e) => handleChange('empresa_nombre', e.target.value)}
                      style={{ width: '100%' }}
                    />
                    <span className="formato-hint">Aparece en el encabezado del formato</span>
                  </div>

                  <div className="formato-control-group">
                    <div className="setting-row">
                      <span>Mostrar logo en encabezado</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.empresa_formato_mostrar_logo !== false && configuraciones.empresa_formato_mostrar_logo !== 'false'}
                          onChange={(e) => handleChange('empresa_formato_mostrar_logo', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  <div className="formato-control-group">
                    <label>Tamaño del logo</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="range"
                        min="40"
                        max="200"
                        step="5"
                        value={configuraciones.empresa_formato_logo_tamano || 100}
                        onChange={(e) => handleChange('empresa_formato_logo_tamano', parseInt(e.target.value))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '40px' }}>{configuraciones.empresa_formato_logo_tamano || 100}px</span>
                    </div>
                  </div>

                  <div className="formato-control-group">
                    <label>Información de la empresa</label>
                    <MiniRichEditor
                      value={configuraciones.empresa_formato_info_empresa || ''}
                      onChange={(val) => handleChange('empresa_formato_info_empresa', val)}
                      placeholder="Dirección, teléfono, RFC, etc."
                      rows={3}
                    />
                    <span className="formato-hint">Aparece junto al logo en el encabezado</span>
                  </div>

                  <div className="formato-control-group">
                    <label>Tamaño letra info empresa</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="range"
                        min="7"
                        max="16"
                        step="1"
                        value={configuraciones.empresa_formato_info_tamano || 9}
                        onChange={(e) => handleChange('empresa_formato_info_tamano', parseInt(e.target.value))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '35px' }}>{configuraciones.empresa_formato_info_tamano || 9}px</span>
                    </div>
                  </div>

                  <div className="formato-control-group">
                    <div className="setting-row">
                      <span>Mostrar marca de agua</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.empresa_formato_marca_agua !== false && configuraciones.empresa_formato_marca_agua !== 'false'}
                          onChange={(e) => handleChange('empresa_formato_marca_agua', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  <div className={`formato-control-group ${configuraciones.empresa_formato_marca_agua === false || configuraciones.empresa_formato_marca_agua === 'false' ? 'disabled' : ''}`}>
                    <label>Opacidad de marca de agua</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="range"
                        min="1"
                        max="15"
                        value={configuraciones.empresa_formato_marca_agua_opacidad || 5}
                        onChange={(e) => handleChange('empresa_formato_marca_agua_opacidad', parseInt(e.target.value))}
                        style={{ flex: 1 }}
                        disabled={configuraciones.empresa_formato_marca_agua === false || configuraciones.empresa_formato_marca_agua === 'false'}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '30px' }}>{configuraciones.empresa_formato_marca_agua_opacidad || 5}%</span>
                    </div>
                  </div>

                  <div className={`formato-control-group ${configuraciones.empresa_formato_marca_agua === false || configuraciones.empresa_formato_marca_agua === 'false' ? 'disabled' : ''}`}>
                    <label>Tamaño de marca de agua</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="range"
                        min="200"
                        max="1500"
                        step="50"
                        value={configuraciones.empresa_formato_marca_agua_tamano || 500}
                        onChange={(e) => handleChange('empresa_formato_marca_agua_tamano', parseInt(e.target.value))}
                        style={{ flex: 1 }}
                        disabled={configuraciones.empresa_formato_marca_agua === false || configuraciones.empresa_formato_marca_agua === 'false'}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '40px' }}>{configuraciones.empresa_formato_marca_agua_tamano || 500}px</span>
                    </div>
                  </div>
                </div>

                {/* Columna 2: Opciones de impresión */}
                <div className="formato-controls">
                  <div className="formato-control-group">
                    <label>Tamaño letra datos cliente</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="range"
                        min="7"
                        max="16"
                        step="1"
                        value={configuraciones.empresa_formato_cliente_tamano || 10}
                        onChange={(e) => handleChange('empresa_formato_cliente_tamano', parseInt(e.target.value))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '35px' }}>{configuraciones.empresa_formato_cliente_tamano || 10}px</span>
                    </div>
                  </div>

                  <div className="formato-control-group">
                    <div className="setting-row">
                      <span>Desglose de IVA</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.empresa_formato_desglose_iva === true || configuraciones.empresa_formato_desglose_iva === 'true'}
                          onChange={(e) => handleChange('empresa_formato_desglose_iva', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <span className="formato-hint">Muestra Subtotal, IVA (16%) y Total en el formato</span>
                  </div>

                  <div className="formato-control-group">
                    <label style={{ marginBottom: '8px' }}>Copias a imprimir</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div className="setting-row">
                        <span>Original</span>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={configuraciones.empresa_formato_copia_original !== false && configuraciones.empresa_formato_copia_original !== 'false'}
                            onChange={(e) => handleChange('empresa_formato_copia_original', e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <div className="setting-row">
                        <span>Copia Cliente</span>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={configuraciones.empresa_formato_copia_cliente === true || configuraciones.empresa_formato_copia_cliente === 'true'}
                            onChange={(e) => handleChange('empresa_formato_copia_cliente', e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <div className="setting-row">
                        <span>Copia Mostrador</span>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={configuraciones.empresa_formato_copia_mostrador === true || configuraciones.empresa_formato_copia_mostrador === 'true'}
                            onChange={(e) => handleChange('empresa_formato_copia_mostrador', e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                    <span className="formato-hint">Cada copia se imprime en una página separada con su etiqueta</span>
                  </div>

                  <div className="formato-control-group">
                    <label>Pie de página personalizado</label>
                    <MiniRichEditor
                      value={configuraciones.empresa_formato_pie_pagina || ''}
                      onChange={(val) => handleChange('empresa_formato_pie_pagina', val)}
                      placeholder="Ej: Gracias por su compra. Precios sujetos a cambio sin previo aviso."
                      rows={2}
                    />
                  </div>

                  <div className="formato-control-group">
                    <div className="setting-row">
                      <span>Mostrar pagaré</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={configuraciones.empresa_formato_pagare === true || configuraciones.empresa_formato_pagare === 'true'}
                          onChange={(e) => handleChange('empresa_formato_pagare', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <span className="formato-hint">Agrega sección de pagaré al pie del formato (ventas a crédito)</span>
                  </div>

                  <div className={`formato-control-group ${configuraciones.empresa_formato_pagare !== true && configuraciones.empresa_formato_pagare !== 'true' ? 'disabled' : ''}`}>
                    <label>Texto legal del pagaré</label>
                    <textarea
                      className="text-input-compact"
                      placeholder="POR EL PRESENTE PAGARÉ RECONOZCO(EMOS) DEBER Y ME(NOS) OBLIGO(AMOS)..."
                      value={configuraciones.empresa_formato_pagare_texto || ''}
                      onChange={(e) => handleChange('empresa_formato_pagare_texto', e.target.value)}
                      rows={4}
                      style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '11px' }}
                      disabled={configuraciones.empresa_formato_pagare !== true && configuraciones.empresa_formato_pagare !== 'true'}
                    />
                    <span className="formato-hint">Variables: {'{ciudad}'}, {'{fecha_emision}'}, {'{empresa}'}, {'{importe}'}, {'{importe_letra}'}, {'{fecha_vencimiento}'}</span>
                  </div>
                </div>
                </div>

                {/* Preview 50% */}
                <div className="formato-preview-container">
                  <div className="formato-preview-label">Vista previa</div>
                  <div className="formato-preview-wrapper-compact">
                    <iframe
                      className="formato-preview-iframe"
                      srcDoc={generarPreviewFormatoHTML()}
                      title="Preview formato"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Sub-tab: Diseño Ticket */}
            {cajaSubTab === 'ticket' && (
              <div className="ticket-designer">
                {/* Controles del ticket */}
                <div className="formato-controls">
                  <div className="formato-control-group">
                    <label>Mostrar logo</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={configuraciones.impresion_mostrar_logo === true || configuraciones.impresion_mostrar_logo === 'true'}
                        onChange={(e) => handleChange('impresion_mostrar_logo', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="formato-control-group">
                    <label>Encabezado del ticket</label>
                    <textarea
                      className="text-input-compact"
                      placeholder="Texto en la parte superior"
                      value={configuraciones.impresion_encabezado_ticket || ''}
                      onChange={(e) => handleChange('impresion_encabezado_ticket', e.target.value)}
                      rows={3}
                      style={{ width: '100%', resize: 'vertical', fontFamily: "'Courier New', monospace", fontSize: '12px' }}
                    />
                  </div>

                  <div className="formato-control-group">
                    <label>Pie del ticket</label>
                    <textarea
                      className="text-input-compact"
                      placeholder="Texto en la parte inferior"
                      value={configuraciones.impresion_pie_ticket || ''}
                      onChange={(e) => handleChange('impresion_pie_ticket', e.target.value)}
                      rows={3}
                      style={{ width: '100%', resize: 'vertical', fontFamily: "'Courier New', monospace", fontSize: '12px' }}
                    />
                  </div>

                  <div className="formato-control-group">
                    <label>Mostrar código de barras</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={configuraciones.impresion_codigo_barras === true || configuraciones.impresion_codigo_barras === 'true'}
                        onChange={(e) => handleChange('impresion_codigo_barras', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="formato-control-group">
                    <label>Desglose de IVA</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={configuraciones.impresion_desglose_iva === true || configuraciones.impresion_desglose_iva === 'true'}
                        onChange={(e) => handleChange('impresion_desglose_iva', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="formato-control-group">
                    <label>Total en letra</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={configuraciones.impresion_total_en_letra === true || configuraciones.impresion_total_en_letra === 'true'}
                        onChange={(e) => handleChange('impresion_total_en_letra', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="formato-control-group">
                    <label>Margen izquierdo (mm)</label>
                    <input
                      type="number"
                      className="number-input compact"
                      min="0"
                      max="20"
                      value={configuraciones.impresion_margen_izquierdo ?? 4}
                      onChange={(e) => handleChange('impresion_margen_izquierdo', parseInt(e.target.value) || 0)}
                    />
                  </div>

                  <div className="formato-control-group">
                    <label>Copias por ticket</label>
                    <input
                      type="number"
                      className="number-input compact"
                      min="1"
                      max="5"
                      value={configuraciones.impresion_copias_ticket || 1}
                      onChange={(e) => handleChange('impresion_copias_ticket', parseInt(e.target.value) || 1)}
                    />
                  </div>

                  <div className="formato-control-group">
                    <label>Leyenda de facturación</label>
                    <textarea
                      className="text-input-compact"
                      placeholder="Ej: Solicita tu factura en nuestro portal"
                      value={configuraciones.impresion_leyenda_factura || ''}
                      onChange={(e) => handleChange('impresion_leyenda_factura', e.target.value)}
                      rows={2}
                      style={{ width: '100%', resize: 'vertical', fontFamily: "'Courier New', monospace", fontSize: '12px' }}
                    />
                  </div>

                  <div className="formato-control-group">
                    <label>URL portal de facturación</label>
                    <input
                      type="text"
                      className="text-input-compact"
                      placeholder="https://facturacion.miempresa.com"
                      value={configuraciones.impresion_portal_facturacion_url || ''}
                      onChange={(e) => handleChange('impresion_portal_facturacion_url', e.target.value)}
                      style={{ width: '100%', fontFamily: "'Courier New', monospace", fontSize: '12px' }}
                    />
                  </div>

                  <div className="formato-control-group">
                    <label>Mostrar QR del portal</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={configuraciones.impresion_mostrar_qr_portal === true || configuraciones.impresion_mostrar_qr_portal === 'true'}
                        onChange={(e) => handleChange('impresion_mostrar_qr_portal', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                {/* Preview del ticket */}
                <div className="formato-preview-container">
                  <span className="formato-preview-label">Vista previa del ticket</span>
                  <div className="ticket-preview-wrapper">
                    <iframe
                      className="ticket-preview-iframe"
                      srcDoc={generarPreviewTicketHTML()}
                      title="Preview ticket"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Sub-tab: Facturación (Emisores CFDI) */}
            {cajaSubTab === 'facturacion' && canConfigFacturacion && (
              <div>
                  {/* URL del servicio de facturación */}
                  <div className="settings-section">
                    <div className="section-header">
                      <Globe size={20} />
                      <h2>Servicio de Facturación</h2>
                    </div>
                    <div className="settings-card">
                      <div className="setting-item-compact global-setting">
                        <div className="global-badge"><Globe size={10} /> Global</div>
                        <div className="setting-row">
                          <span>URL del servicio de facturación</span>
                          <input
                            type="text"
                            className="text-input"
                            style={{ flex: 1, maxWidth: '500px' }}
                            value={configuraciones.empresa_facturacion_url || ''}
                            onChange={(e) => handleChange('empresa_facturacion_url', e.target.value)}
                            placeholder="https://api.facturacion.com/v1"
                          />
                        </div>
                        <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          URL base del servicio externo de facturación CFDI. Se guarda como configuración global.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Emisores */}
                  <div className="settings-section">
                    <div className="section-header">
                      <FileText size={20} />
                      <h2>Emisores CFDI</h2>
                    </div>
                    <p style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                      Administre los emisores disponibles para facturación. Al crear una factura podrá seleccionar cuál usar.
                    </p>

                    {/* Formulario agregar/editar */}
                    <div className="settings-card" style={{ marginBottom: '1rem' }}>
                      <div className="setting-item-compact">
                        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                          {editandoEmisor ? 'Editar Emisor' : 'Agregar Emisor'}
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <div style={{ flex: 2, minWidth: '200px' }}>
                            <label className="setting-label">Llave (API Key) <span style={{ color: '#ef4444' }}>*</span></label>
                            <input
                              type="text"
                              className={`text-input${emisorErrors.llave ? ' input-error' : ''}`}
                              value={nuevoEmisor.llave}
                              onChange={(e) => {
                                setNuevoEmisor(prev => ({ ...prev, llave: e.target.value }));
                                if (emisorErrors.llave) setEmisorErrors(prev => ({ ...prev, llave: null }));
                              }}
                              placeholder="Llave del servicio"
                            />
                            {emisorErrors.llave && <span className="error-text">{emisorErrors.llave}</span>}
                          </div>
                          <div style={{ flex: 1, minWidth: '140px' }}>
                            <label className="setting-label">RFC <span style={{ color: '#ef4444' }}>*</span></label>
                            <input
                              type="text"
                              className={`text-input${emisorErrors.rfc ? ' input-error' : ''}`}
                              value={nuevoEmisor.rfc}
                              onChange={(e) => {
                                setNuevoEmisor(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }));
                                if (emisorErrors.rfc) setEmisorErrors(prev => ({ ...prev, rfc: null }));
                              }}
                              placeholder="XAXX010101000"
                              maxLength={13}
                            />
                            {emisorErrors.rfc && <span className="error-text">{emisorErrors.rfc}</span>}
                          </div>
                          <div style={{ flex: 2, minWidth: '200px' }}>
                            <label className="setting-label">Razón Social <span style={{ color: '#ef4444' }}>*</span></label>
                            <input
                              type="text"
                              className={`text-input${emisorErrors.razon_social ? ' input-error' : ''}`}
                              value={nuevoEmisor.razon_social}
                              onChange={(e) => {
                                setNuevoEmisor(prev => ({ ...prev, razon_social: e.target.value }));
                                if (emisorErrors.razon_social) setEmisorErrors(prev => ({ ...prev, razon_social: null }));
                              }}
                              placeholder="Mi Empresa S.A. de C.V."
                            />
                            {emisorErrors.razon_social && <span className="error-text">{emisorErrors.razon_social}</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', paddingBottom: '1px' }}>
                            {editandoEmisor && (
                              <Button variant="outline" onClick={handleCancelarEdicion} disabled={savingEmisor}>
                                Cancelar
                              </Button>
                            )}
                            <Button
                              variant="primary"
                              onClick={editandoEmisor ? handleGuardarEdicion : handleAgregarEmisor}
                              disabled={savingEmisor}
                            >
                              {savingEmisor ? 'Guardando...' : editandoEmisor ? 'Guardar Cambios' : 'Agregar'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Lista de emisores */}
                    <div className="settings-card">
                      {loadingEmisores ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                          <RefreshCw size={16} className="spin" style={{ marginRight: '6px' }} />
                          Cargando emisores...
                        </div>
                      ) : emisores.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
                          No hay emisores configurados. Agregue uno arriba.
                        </div>
                      ) : (
                        <table className="emisores-table">
                          <thead>
                            <tr>
                              <th>RFC</th>
                              <th>Razón Social</th>
                              <th>Llave</th>
                              <th style={{ width: '80px', textAlign: 'center' }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emisores.map(emisor => (
                              <tr key={emisor.id} className={editandoEmisor === emisor.id ? 'editing' : ''}>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{emisor.rfc}</td>
                                <td>{emisor.razon_social}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>
                                  {emisor.llave.substring(0, 12)}...
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                    <button
                                      className="btn-icon-sm"
                                      onClick={() => handleEditarEmisor(emisor)}
                                      title="Editar"
                                      disabled={editandoEmisor !== null}
                                    >
                                      <Settings size={13} />
                                    </button>
                                    <button
                                      className="btn-icon-sm danger"
                                      onClick={() => handleEliminarEmisor(emisor.id)}
                                      title="Eliminar"
                                      disabled={editandoEmisor !== null}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
              </div>
            )}
          </div>
        )}

        {/* ====== TAB: ADMINISTRACIÓN ====== */}
        {activeTab === 'admin' && canViewAdmin && (
          <div>
            {/* Sub-tabs de administración */}
            <div className="settings-subtabs">
              {!isWebMode && (
              <button
                className={`settings-subtab ${adminSubTab === 'punto-venta' ? 'active' : ''}`}
                onClick={() => setAdminSubTab('punto-venta')}
              >
                <Monitor size={15} />
                Punto de Venta
              </button>
              )}
              {!isWebMode && (
              <button
                className={`settings-subtab ${adminSubTab === 'api' ? 'active' : ''}`}
                onClick={() => setAdminSubTab('api')}
              >
                <Globe size={15} />
                Conexión API
              </button>
              )}
              {!isWebMode && (
              <button
                className={`settings-subtab ${adminSubTab === 'sync' ? 'active' : ''}`}
                onClick={() => setAdminSubTab('sync')}
              >
                <Cloud size={15} />
                Sincronización
              </button>
              )}
              <button
                className={`settings-subtab ${adminSubTab === 'web-mode' ? 'active' : ''}`}
                onClick={() => setAdminSubTab('web-mode')}
              >
                <Globe size={15} />
                Modo Web
              </button>
            </div>

            {/* ---- Sub-tab: Punto de Venta ---- */}
            {adminSubTab === 'punto-venta' && (
            <div className="settings-admin-grid">
              {/* Columna izquierda: Info PV */}
              <div>
                <div className="settings-section">
                  <div className="section-header">
                    <Monitor size={20} />
                    <h2>Punto de Venta</h2>
                  </div>

                  <div className="settings-card">
                    <div className="setting-item-compact">
                      <div className="setting-row">
                        <span>Nombre del Equipo</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            className="text-input-compact"
                            placeholder="Nombre del equipo"
                            value={infoPc.nombrePc}
                            onChange={(e) => {
                              const newNombrePc = e.target.value;
                              setInfoPc(prev => ({ ...prev, nombrePc: newNombrePc }));
                              localStorage.setItem('pos_nombre_pc', newNombrePc);
                            }}
                            style={{ width: '180px' }}
                          />
                          <button
                            onClick={obtenerInfoPc}
                            disabled={loadingInfoPc}
                            style={{
                              background: 'none',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              padding: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Detectar automáticamente"
                          >
                            <RefreshCw size={14} className={loadingInfoPc ? 'spinning' : ''} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="setting-item-compact">
                      <div className="setting-row">
                        <span>Dirección IP</span>
                        <span style={{
                          fontSize: '13px',
                          color: '#374151',
                          fontWeight: 500,
                          background: '#f3f4f6',
                          padding: '6px 12px',
                          borderRadius: '6px'
                        }}>
                          {loadingInfoPc ? 'Cargando...' : (infoPc.ip || 'No disponible')}
                        </span>
                      </div>
                    </div>

                    <div className="setting-item-compact">
                      <div className="setting-row">
                        <span>Identificador de Caja</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            className="text-input-compact"
                            placeholder="Ej: Caja 1"
                            value={infoPc.identificador}
                            onChange={(e) => setInfoPc(prev => ({ ...prev, identificador: e.target.value }))}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            style={{ width: '150px' }}
                            disabled={!infoPc.nombrePc}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Columna derecha: Báscula */}
              <div>
                <div className="settings-section">
                  <div className="section-header">
                    <Scale size={20} />
                    <h2>Báscula</h2>
                  </div>

                  <div className="settings-card">
                    {/* Puerto COM */}
                    <div className="setting-item-compact">
                      <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <span>Puerto COM</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                          <select
                            value={configuraciones.caja_bascula_puerto || ''}
                            onChange={(e) => handleChange('caja_bascula_puerto', e.target.value)}
                            disabled={scaleConnected}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb',
                              fontSize: '13px',
                              backgroundColor: scaleConnected ? '#f3f4f6' : 'white'
                            }}
                          >
                            {scalePorts.length === 0 && <option value="">No se encontraron puertos</option>}
                            {scalePorts.map(p => (
                              <option key={p.path} value={p.path}>
                                {p.path} - {p.manufacturer}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={cargarPuertosCOM}
                            disabled={scaleConnected}
                            style={{
                              background: 'none',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              padding: '8px',
                              cursor: scaleConnected ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Refrescar puertos"
                          >
                            <RefreshCw size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Baud Rate */}
                    <div className="setting-item-compact">
                      <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <span>Velocidad (Baud Rate)</span>
                        <select
                          value={configuraciones.caja_bascula_baudrate || '9600'}
                          onChange={(e) => handleChange('caja_bascula_baudrate', e.target.value)}
                          disabled={scaleConnected}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            fontSize: '13px',
                            backgroundColor: scaleConnected ? '#f3f4f6' : 'white'
                          }}
                        >
                          <option value={9600}>9600</option>
                          <option value={115200}>115200</option>
                        </select>
                      </div>
                    </div>

                    {/* Conectar / Desconectar */}
                    <div className="setting-item-compact">
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {!scaleConnected ? (
                          <button
                            onClick={handleScaleConnect}
                            disabled={scaleConnecting || !configuraciones.caja_bascula_puerto}
                            style={{
                              background: scaleConnecting ? '#9ca3af' : '#10b981',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 20px',
                              color: 'white',
                              cursor: scaleConnecting ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {scaleConnecting ? (
                              <>
                                <Loader size={14} className="spinning" />
                                Conectando...
                              </>
                            ) : (
                              'Conectar'
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={handleScaleDisconnect}
                            style={{
                              background: '#ef4444',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 20px',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 500
                            }}
                          >
                            Desconectar
                          </button>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: scaleConnected ? '#10b981' : '#9ca3af'
                          }} />
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            {scaleConnected ? 'Conectada' : 'Desconectada'}
                          </span>
                        </div>
                      </div>
                      {scaleStatus && (
                        <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                          {scaleStatus}
                        </div>
                      )}
                    </div>

                    {/* Leer Peso */}
                    {scaleConnected && (
                      <div className="setting-item-compact">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <button
                            onClick={handleReadWeight}
                            disabled={readingWeight}
                            style={{
                              background: readingWeight ? '#9ca3af' : '#3b82f6',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 20px',
                              color: 'white',
                              cursor: readingWeight ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              alignSelf: 'flex-start'
                            }}
                          >
                            {readingWeight ? (
                              <>
                                <Loader size={14} className="spinning" />
                                Leyendo...
                              </>
                            ) : (
                              <>
                                <Scale size={14} />
                                Leer Peso
                              </>
                            )}
                          </button>
                          {scaleWeight && (
                            <div style={{
                              fontFamily: 'monospace',
                              fontSize: '32px',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              padding: '16px 24px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              color: scaleWeight.stable ? '#111827' : '#f59e0b'
                            }}>
                              {scaleWeight.display}
                              {scaleWeight.stable !== undefined && (
                                <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginTop: '4px' }}>
                                  {scaleWeight.stable ? 'Estable' : 'Inestable'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* ---- Sub-tab: Conexión API ---- */}
            {adminSubTab === 'api' && (
            <div>
            <div className="settings-section">
              <div className="section-header">
                <Globe size={20} />
                <h2>Conexión al Servidor</h2>
              </div>

              <div className="settings-card">
                <div className="setting-item-compact">
                  <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                    <span>URL del API</span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                      <input
                        type="text"
                        className="text-input-compact"
                        placeholder="http://localhost/api_pos/reportes/funciones_php/api.php"
                        value={apiUrl}
                        onChange={(e) => {
                          setApiUrl(e.target.value);
                          setApiTestResult(null);
                        }}
                        style={{ flex: 1, minWidth: '300px' }}
                      />
                      <button
                        onClick={handleTestApiConnection}
                        disabled={testingApi || !apiUrl.trim()}
                        style={{
                          background: testingApi ? '#9ca3af' : '#3b82f6',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          color: 'white',
                          cursor: testingApi ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                        title="Probar conexión"
                      >
                        {testingApi ? (
                          <>
                            <Loader size={14} className="spinning" />
                            Probando...
                          </>
                        ) : (
                          <>
                            <RefreshCw size={14} />
                            Probar
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRestoreDefaultApiUrl}
                        style={{
                          background: 'none',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}
                        title="Restaurar valor por defecto"
                      >
                        Por defecto
                      </button>
                      {apiTestResult && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {apiTestResult === 'success' ? (
                            <CheckCircle size={20} color="#10b981" />
                          ) : (
                            <XCircle size={20} color="#ef4444" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                    URL completa del servidor API. Esta configuración se guarda en un archivo local (api_config.txt).
                    {apiUrl !== apiUrlOriginal && (
                      <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
                        (Cambio pendiente de guardar)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
            )}

            {/* ---- Sub-tab: Sincronización ---- */}
            {adminSubTab === 'sync' && (
            <div className="settings-admin-grid">
              {/* Columna izquierda: Configuración Nube */}
              <div>
                <div className="settings-section">
                  <div className="section-header">
                    <Cloud size={20} />
                    <h2>Sincronización con Nube</h2>
                  </div>

                  <div className="settings-card">
                    {/* URL API Nube */}
                    <div className="setting-item-compact">
                      <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <span>URL del API Nube</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                          <input
                            type="text"
                            className="text-input-compact"
                            placeholder="https://tu-servidor.com/api.php"
                            value={configuraciones.caja_api_nube_url || ''}
                            onChange={(e) => {
                              handleChange('caja_api_nube_url', e.target.value);
                              setApiNubeTestResult(null);
                            }}
                            style={{ flex: 1, minWidth: '200px' }}
                          />
                          <button
                            onClick={handleTestApiNube}
                            disabled={testingApiNube || !(configuraciones.caja_api_nube_url || '').trim()}
                            style={{
                              background: testingApiNube ? '#9ca3af' : '#3b82f6',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              color: 'white',
                              cursor: testingApiNube ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px',
                              fontWeight: 500
                            }}
                            title="Probar conexión"
                          >
                            {testingApiNube ? (
                              <>
                                <Loader size={14} className="spinning" />
                                Probando...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={14} />
                                Probar
                              </>
                            )}
                          </button>
                          {apiNubeTestResult && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {apiNubeTestResult === 'success' ? (
                                <CheckCircle size={20} color="#10b981" />
                              ) : (
                                <XCircle size={20} color="#ef4444" />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Negocio */}
                    <div className="setting-item-compact">
                      <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <span>Negocio</span>
                        <input
                          type="text"
                          className="text-input-compact"
                          placeholder="Ej: abarrotes_martinez"
                          value={configuraciones.caja_api_nube_negocio || ''}
                          onChange={(e) => handleChange('caja_api_nube_negocio', e.target.value)}
                          style={{ width: '100%', maxWidth: '400px' }}
                        />
                      </div>
                    </div>

                    {/* Sucursal */}
                    <div className="setting-item-compact">
                      <div className="setting-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <span>Sucursal</span>
                        <input
                          type="text"
                          className="text-input-compact"
                          placeholder="Ej: sucursal_centro"
                          value={configuraciones.caja_api_nube_sucursal || ''}
                          onChange={(e) => handleChange('caja_api_nube_sucursal', e.target.value)}
                          style={{ width: '100%', maxWidth: '400px' }}
                        />
                      </div>
                    </div>

                    <div className="setting-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                      Configuración para sincronización master-master con la base de datos en la nube.
                      {((configuraciones.caja_api_nube_url || '') !== (configOriginal.caja_api_nube_url || '') ||
                        (configuraciones.caja_api_nube_negocio || '') !== (configOriginal.caja_api_nube_negocio || '') ||
                        (configuraciones.caja_api_nube_sucursal || '') !== (configOriginal.caja_api_nube_sucursal || '')) && (
                        <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
                          (Cambio pendiente de guardar)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Columna derecha: Panel de Control */}
              <div>
                {hasSyncConfig() && (
                <div className="settings-section">
                  <div className="section-header">
                    <RefreshCw size={20} />
                    <h2>Panel de Control</h2>
                  </div>

                  <div className="settings-card">
                    {/* Botones de sync manual */}
                    <div className="setting-item-compact">
                      <span style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', display: 'block' }}>Sincronización Manual</span>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={handleSyncPush}
                          disabled={syncing}
                          style={{
                            background: syncing && syncOperation === 'push' ? '#9ca3af' : '#3b82f6',
                            border: 'none', borderRadius: '6px', padding: '8px 16px',
                            color: 'white', cursor: syncing ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '13px', fontWeight: 500
                          }}
                        >
                          {syncing && syncOperation === 'push' ? (
                            <><Loader size={14} className="spinning" /> Enviando...</>
                          ) : (
                            <><ArrowUpCircle size={14} /> Push (Enviar)</>
                          )}
                        </button>
                        <button
                          onClick={handleSyncPull}
                          disabled={syncing}
                          style={{
                            background: syncing && syncOperation === 'pull' ? '#9ca3af' : '#8b5cf6',
                            border: 'none', borderRadius: '6px', padding: '8px 16px',
                            color: 'white', cursor: syncing ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '13px', fontWeight: 500
                          }}
                        >
                          {syncing && syncOperation === 'pull' ? (
                            <><Loader size={14} className="spinning" /> Recibiendo...</>
                          ) : (
                            <><ArrowDownCircle size={14} /> Pull (Recibir)</>
                          )}
                        </button>
                        <button
                          onClick={handleSyncFull}
                          disabled={syncing}
                          style={{
                            background: syncing && syncOperation === 'full' ? '#9ca3af' : '#10b981',
                            border: 'none', borderRadius: '6px', padding: '8px 16px',
                            color: 'white', cursor: syncing ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '13px', fontWeight: 500
                          }}
                        >
                          {syncing && syncOperation === 'full' ? (
                            <><Loader size={14} className="spinning" /> Sincronizando...</>
                          ) : (
                            <><RefreshCw size={14} /> Sync Completo</>
                          )}
                        </button>
                        <button
                          onClick={handleSyncReset}
                          disabled={syncing}
                          style={{
                            background: '#ef4444',
                            border: 'none', borderRadius: '6px', padding: '8px 16px',
                            color: 'white', cursor: syncing ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '13px', fontWeight: 500
                          }}
                          title="Resetear toda la sincronización"
                        >
                          <RotateCcw size={14} /> Reset
                        </button>
                      </div>
                    </div>

                    {/* Último resultado */}
                    {syncResult && (
                      <div className="setting-item-compact" style={{ marginTop: '12px' }}>
                        <div style={{
                          padding: '10px 14px',
                          borderRadius: '8px',
                          backgroundColor: syncResult.success ? '#ecfdf5' : '#fef2f2',
                          border: `1px solid ${syncResult.success ? '#a7f3d0' : '#fecaca'}`,
                          fontSize: '13px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            {syncResult.success ? <CheckCircle size={16} color="#10b981" /> : <XCircle size={16} color="#ef4444" />}
                            <span style={{ fontWeight: 600 }}>{syncResult.success ? 'Sync exitosa' : 'Error en sync'}</span>
                          </div>
                          {syncResult.data?.push?.resumen && (
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>
                              Push: {syncResult.data.push.resumen.total_enviados || 0} enviados, {syncResult.data.push.resumen.tablas_procesadas || 0} tablas
                            </div>
                          )}
                          {syncResult.data?.pull?.resumen && (
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>
                              Pull: {syncResult.data.pull.resumen.total_recibidos || 0} recibidos, {syncResult.data.pull.resumen.tablas_procesadas || 0} tablas
                            </div>
                          )}
                          {syncResult.data?.resumen && !syncResult.data?.push && (
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>
                              {syncResult.data.resumen.total_enviados > 0 ? `Enviados: ${syncResult.data.resumen.total_enviados}` : ''}
                              {syncResult.data.resumen.total_recibidos > 0 ? ` Recibidos: ${syncResult.data.resumen.total_recibidos}` : ''}
                              {syncResult.data.resumen.duracion_total_ms ? ` (${(syncResult.data.resumen.duracion_total_ms / 1000).toFixed(1)}s)` : ''}
                            </div>
                          )}
                          {!syncResult.success && syncResult.message && (
                            <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{syncResult.message}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Timer automático */}
                    <div className="setting-item-compact" style={{ marginTop: '16px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                        <Clock size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                        Sync Automática
                      </span>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>Cada</span>
                          <select
                            value={autoSyncInterval}
                            onChange={(e) => syncCtx.setIntervalMin(parseInt(e.target.value, 10))}
                            disabled={autoSyncEnabled}
                            style={{
                              padding: '6px 10px', borderRadius: '6px',
                              border: '1px solid #e5e7eb', fontSize: '13px',
                              backgroundColor: autoSyncEnabled ? '#f3f4f6' : 'white'
                            }}
                          >
                            <option value={1}>1 min</option>
                            <option value={2}>2 min</option>
                            <option value={5}>5 min</option>
                            <option value={10}>10 min</option>
                            <option value={15}>15 min</option>
                            <option value={30}>30 min</option>
                            <option value={60}>1 hora</option>
                          </select>
                        </div>

                        {!autoSyncEnabled ? (
                          <button
                            onClick={startAutoSync}
                            disabled={syncing}
                            style={{
                              background: '#10b981', border: 'none', borderRadius: '6px',
                              padding: '8px 16px', color: 'white',
                              cursor: syncing ? 'not-allowed' : 'pointer',
                              display: 'flex', alignItems: 'center', gap: '6px',
                              fontSize: '13px', fontWeight: 500
                            }}
                          >
                            <Play size={14} /> Activar
                          </button>
                        ) : (
                          <button
                            onClick={stopAutoSync}
                            style={{
                              background: '#f59e0b', border: 'none', borderRadius: '6px',
                              padding: '8px 16px', color: 'white', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '6px',
                              fontSize: '13px', fontWeight: 500
                            }}
                          >
                            <Pause size={14} /> Detener
                          </button>
                        )}

                        {autoSyncEnabled && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '6px 12px', borderRadius: '20px',
                            backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0'
                          }}>
                            <div style={{
                              width: '8px', height: '8px', borderRadius: '50%',
                              backgroundColor: '#10b981',
                              animation: 'pulse 2s infinite'
                            }} />
                            <span style={{ fontSize: '13px', color: '#059669', fontWeight: 500 }}>
                              Activo - Próximo en {formatCountdown(autoSyncCountdown)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Estado de sync por tabla */}
                    {syncStatus && (
                      <div className="setting-item-compact" style={{ marginTop: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>Estado</span>
                          <button
                            onClick={cargarSyncStatus}
                            disabled={loadingSyncStatus}
                            style={{
                              background: 'none', border: '1px solid #e5e7eb',
                              borderRadius: '6px', padding: '4px 8px', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px'
                            }}
                          >
                            <RefreshCw size={12} className={loadingSyncStatus ? 'spinning' : ''} /> Actualizar
                          </button>
                        </div>

                        <div style={{
                          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '8px', fontSize: '13px'
                        }}>
                          <div style={{
                            padding: '10px', borderRadius: '8px',
                            backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '20px', fontWeight: 700, color: '#2563eb' }}>
                              {syncStatus.tablas_con_push || 0}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>Push</div>
                          </div>
                          <div style={{
                            padding: '10px', borderRadius: '8px',
                            backgroundColor: '#f5f3ff', border: '1px solid #c4b5fd', textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '20px', fontWeight: 700, color: '#7c3aed' }}>
                              {syncStatus.tablas_con_pull || 0}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>Pull</div>
                          </div>
                          <div style={{
                            padding: '10px', borderRadius: '8px',
                            backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '20px', fontWeight: 700, color: '#059669' }}>
                              {syncStatus.total_tablas || 0}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>Total</div>
                          </div>
                        </div>

                        {syncStatus.ultimo_push && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                            Push: <strong>{formatSyncTime(syncStatus.ultimo_push)}</strong>
                            {syncStatus.ultimo_pull && (<> | Pull: <strong>{formatSyncTime(syncStatus.ultimo_pull)}</strong></>)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Historial de sync reciente */}
                    {syncLog.length > 0 && (
                      <div className="setting-item-compact" style={{ marginTop: '16px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', display: 'block' }}>Historial Reciente</span>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                                <th style={{ padding: '4px 8px', textAlign: 'left' }}>Fecha</th>
                                <th style={{ padding: '4px 8px', textAlign: 'left' }}>Tabla</th>
                                <th style={{ padding: '4px 8px', textAlign: 'center' }}>Dir</th>
                                <th style={{ padding: '4px 8px', textAlign: 'right' }}>Reg</th>
                                <th style={{ padding: '4px 8px', textAlign: 'center' }}>OK</th>
                              </tr>
                            </thead>
                            <tbody>
                              {syncLog.map((log, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '4px 8px' }}>{formatSyncTime(log.created_at)}</td>
                                  <td style={{ padding: '4px 8px' }}>{log.tabla}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                    {log.direccion === 'push' ? (
                                      <ArrowUpCircle size={14} color="#3b82f6" title="Push" />
                                    ) : (
                                      <ArrowDownCircle size={14} color="#8b5cf6" title="Pull" />
                                    )}
                                  </td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                                    {log.registros_enviados || log.registros_recibidos || 0}
                                  </td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                    {log.estado === 'completado' ? (
                                      <CheckCircle size={14} color="#10b981" />
                                    ) : log.estado === 'error' ? (
                                      <XCircle size={14} color="#ef4444" />
                                    ) : (
                                      <Loader size={14} color="#f59e0b" />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
            )}

            {/* ---- Sub-tab: Modo Web ---- */}
            {adminSubTab === 'web-mode' && (
              <div className="settings-section">
                <WebModeConfig />
              </div>
            )}

          </div>
        )}

        {/* Sin permisos */}
        {!canViewCaja && !canViewAdmin && (
          <div className="settings-no-access">
            <ShieldCheck size={48} />
            <h3>Sin acceso</h3>
            <p>No tienes permisos para ver las configuraciones del sistema.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default SettingsPage;
